<script setup>
// =============================================================================
// FangateVaultView.vue — Fullscreen Fangate vault for a specific inbox.
//
// Renders when the user picks a Fangate account from the sidebar dropdown.
// Replaces the chat list pane + chat area in the layout. Has its own left
// module sidebar (Upload / Dashboard / Content Library / Collection / Wallet
// / Collaborations) and renders the active module on the right.
//
// Data binding: each module fetches via FangateAPI scoped to props.inboxId.
// The FangateAPI client reads the per-inbox token from localStorage via
// setActiveInbox(id), so the same client serves both this view and the
// per-chat FangatePanel side panel.
//
// Modules backed by real API:
//   - Dashboard       → getDashboardSummary
//   - Content Library → listProducts
//   - Wallet          → getWallet
// Modules without an API yet (placeholder "coming soon" cards):
//   - Upload Content  → opens an upload form (reuses prototype layout)
//   - Collection      → coming soon
//   - Collaborations  → coming soon
// =============================================================================

import { ref, reactive, computed, watch, watchEffect, onMounted, onUnmounted, nextTick } from 'vue';
import FangateAPI from 'dashboard/api/fangate';
import { wrap as cacheWrap, wrapSWR as cacheSWR,
         invalidate as cacheInvalidate,
         invalidatePrefix as cacheInvalidatePrefix,
         check as cacheCheck,
         read as cacheRead, write as cacheWrite }
  from 'dashboard/composables/useFangateCache';
import { groupProductsByMedia } from './composables/useFangateVariants';
import FangateContentToolbar from './components/FangateContentToolbar.vue';
import FangateUploadModal from './components/FangateUploadModal.vue';
import FangatePriceModal from './components/FangatePriceModal.vue';
import FangateCollaborations from './components/FangateCollaborations.vue';

const props = defineProps({
  /** Inbox object (id, name, avatar_url, …) — drives token scope + display */
  account: { type: Object, required: true },
  /** Mobile (≤768px) → single-pane: module landing ↔ module content drill-down */
  isMobile: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);

// ---- Module list (left sidebar) --------------------------------------------
// Dashboard was removed — its KPIs were 403'ing for tokens without
// can_view_transactions, and the same data shows up per-product anyway.
// `primary: true` flags Upload as the lead (colored differently + larger).
const MODULES = [
  { id: 'upload',         label: 'Upload Content',  icon: 'upload',    primary: true },
  { id: 'content-library',label: 'Content Library', icon: 'library' },
  { id: 'collection',     label: 'Collection',      icon: 'folder' },
  { id: 'wallet',         label: 'Wallet',          icon: 'wallet' },
  { id: 'collaborations', label: 'Collaborations',  icon: 'users' },
];

// No module is active by default for FIRST-visit accounts — clicking the
// account in the sidebar reveals the module nav but leaves the right pane
// empty until the user explicitly picks a module. Saves the Content Library
// round-trip if they only wanted to glance at the account or pick a
// different module (e.g. Wallet).
//
// On RETURN visits, the whole Vault UI state (active module + Library
// search + folder + sort + type filters + moderation filter + current
// page) is restored from localStorage so the flow "Sven → Library → page 3
// → chat → Sven" lands you back exactly where you left off. Combined with
// the per-account products cache, the Library re-renders instantly.
const activeModule = ref(null);

// One localStorage entry per account holds the entire Vault UI snapshot
// (read once on account-switch, written on every state mutation via the
// watchEffect below). Bundling avoids 6 separate watch handlers and keeps
// the schema obvious — if we ever rename a field, bump the `v1` suffix.
const vaultUiStateKey = id => `fangate_vault_ui_v1_${id}`;

const readVaultUiState = id => {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(vaultUiStateKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
};

// ---- Token scope + data fetch lifecycle ------------------------------------
// Switch the FangateAPI active inbox whenever the account prop changes so
// subsequent calls read the right per-inbox token. The actual refetch is
// hooked up further down (after refreshActive is declared) to avoid a
// temporal-dead-zone error from the `immediate: true` watcher.
const bindAccount = id => { FangateAPI.setActiveInbox(id || null); };

// ---- Data state ------------------------------------------------------------
const isLoading = ref(false);
const fetchError = ref('');
// Permission-denied state: Fangate returns 403 with "This API key cannot
// access this endpoint." when the token lacks the right ability (e.g.
// /dashboard/summary + /wallet need `can_view_transactions`). We surface
// that as its own UI state with a fix link, not a generic red error.
const permissionDenied = ref(false);

const is403 = e => {
  const msg = String(e?.message || e || '');
  return msg.includes('HTTP 403') || msg.toLowerCase().includes('cannot access');
};

const products = ref([]);         // /products  — primaries only (clones absorbed)
// Collection products — loaded lazily when the user opens the Collection
// module. Cached on this ref so navigating away and back doesn't trigger
// a refetch unless the user explicitly hits the refresh button.
const collectionProducts = ref([]);
const isLoadingCollection = ref(false);
// Per-primary cluster data. Keyed by primary product id; absent → singleton.
// Each entry holds the full variant list (sorted by id ascending — same
// ordering as groupProductsByMedia returns). Drives the "N links" count +
// price-range display on the grid card AND the variant dropdown in the
// detail view.
const variantsByPrimary = reactive({});
const wallet   = ref(null);       // /wallet

// ----- Toolbar state (folder picker + search + type filter + sort) ----------
// Mirrors the side-panel toolbar so the Library has the same UX. Each value
// is bound via v-model:* to <FangateContentToolbar>.
const search         = ref('');
const selectedFolder = ref(null);
const sortMode       = ref('newest');
const activeFilters  = reactive({ type: new Set() });
// Moderation-status filter for the Library grid. 'all' = no filter; the
// other three buckets collapse the documented Fangate enum values:
//   • approved  → mapVeriffStatus 'approved' | 'manual_appr'
//   • in_review → 'unchecked' (default lifecycle entry, awaiting AI)
//   • rejected  → 'ai_removed' | 'manual_rem'
const moderationFilter = ref('all');
const moderationBucket = key => {
  if (key === 'approved' || key === 'manual_appr') return 'approved';
  if (key === 'unchecked')                          return 'in_review';
  if (key === 'ai_removed' || key === 'manual_rem') return 'rejected';
  return 'unknown';
};
// Pagination — 30 per page keeps the lazy consent-fetch cap predictable
// (max 30 GET /products/{id}/consent-creator-tags per page navigation).
const PER_PAGE      = 30;
const currentPage   = ref(1);

// Gate that the per-account UI-state restore flips on while replaying a
// snapshot from localStorage. Declared up here so the reset-watcher below
// can guard against the snapshot's filter changes triggering an unwanted
// `currentPage = 1`. The full restore + persist plumbing lives further
// down (under the account-watcher block) where the other state lifecycle
// hooks already sit.
const SUPPRESS_PERSIST = ref(false);

const toggleFilter = (group, value) => {
  const set = activeFilters[group];
  if (set.has(value)) set.delete(value);
  else set.add(value);
};
const clearFilters = () => {
  activeFilters.type.clear();
  selectedFolder.value = null;
};

// Unique folder NAMES across all PRIMARY products. Used as the folder
// picker options in the toolbar. `product.folder` can be either a string
// (legacy) or an object `{ id, name, ... }` (current Fangate); we extract
// just the name so the toolbar renders a plain pill, not stringified JSON.
const productFolderName = p => {
  if (!p?.folder) return null;
  if (typeof p.folder === 'string') return p.folder;
  return p.folder.name || null;
};
const folders = computed(() => {
  const set = new Set();
  (products.value || []).forEach(p => {
    const name = productFolderName(p);
    if (name) set.add(name);
  });
  return [...set];
});
const filterCount = computed(() => activeFilters.type.size);

// Apply folder/type/search filters, then sort. Operates on the primary
// products list (already de-cloned by mergeVariants).
const filteredProducts = computed(() => {
  let list = products.value || [];
  if (selectedFolder.value) {
    list = list.filter(p => productFolderName(p) === selectedFolder.value);
  }
  if (activeFilters.type.size) list = list.filter(p => activeFilters.type.has(p.type));
  if (search.value) {
    const q = search.value.toLowerCase();
    list = list.filter(p => (p.title || '').toLowerCase().includes(q));
  }
  if (moderationFilter.value !== 'all') {
    list = list.filter(p => moderationBucket(productModerationKey(p)) === moderationFilter.value);
  }
  return list;
});

const sortedProducts = computed(() => {
  const list = [...filteredProducts.value];
  const ts = p => {
    const raw = p.created_at ?? p.created_at_timestamp ?? p.createdAt ?? p.id ?? 0;
    if (typeof raw === 'string' && /\D/.test(raw)) {
      const ms = Date.parse(raw);
      return Number.isFinite(ms) ? ms : 0;
    }
    return Number(raw) || 0;
  };
  switch (sortMode.value) {
    case 'oldest':     return list.sort((a, b) => ts(a) - ts(b));
    case 'title-asc':  return list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    case 'price-asc':  return list.sort((a, b) => (a.price || 0) - (b.price || 0));
    case 'price-desc': return list.sort((a, b) => (b.price || 0) - (a.price || 0));
    case 'newest':
    default:           return list.sort((a, b) => ts(b) - ts(a));
  }
});

// Top performing products for the Wallet view — by aggregate revenue
// (total_earnings, already in euros after normalize), tie-broken by unlocks then
// link_clicks so popular-but-unpaid products still surface before dead stock.
const topProducts = computed(() =>
  [...(products.value || [])]
    .sort((a, b) =>
      (Number(b.total_earnings) || 0) - (Number(a.total_earnings) || 0) ||
      (Number(b.unlocks) || 0)        - (Number(a.unlocks) || 0) ||
      (Number(b.link_clicks) || 0)    - (Number(a.link_clicks) || 0)
    )
    .slice(0, 8)
);

// Slice the sorted list to the current page. Resets to page 1 whenever any
// filter/sort changes so the user isn't stranded on an empty page.
const totalPages = computed(() => Math.max(1, Math.ceil(sortedProducts.value.length / PER_PAGE)));
const pagedProducts = computed(() => {
  const start = (currentPage.value - 1) * PER_PAGE;
  return sortedProducts.value.slice(start, start + PER_PAGE);
});
watch([search, selectedFolder, sortMode, moderationFilter,
       () => activeFilters.type.size],
      () => {
        // Skip the reset while restoreVaultUiState() is replaying a
        // persisted snapshot — otherwise restoring a saved page number
        // would be clobbered back to 1 by this watcher seeing the search/
        // filter changes from the same restore batch.
        if (SUPPRESS_PERSIST.value) return;
        currentPage.value = 1;
      });
// Clamp the page if products are removed and the current page no longer exists.
watch(totalPages, n => { if (currentPage.value > n) currentPage.value = n; });

const goToPage = n => {
  const target = Math.min(Math.max(1, n), totalPages.value);
  if (target !== currentPage.value) currentPage.value = target;
};
const prevPage = () => goToPage(currentPage.value - 1);
const nextPage = () => goToPage(currentPage.value + 1);

// ============================================================================
// Lazy consent fetching for the visible page
// ============================================================================
// For every product on the CURRENT page where `is_should_consent: true`, we
// fetch GET /products/{id}/consent-creator-tags and cache the aggregate
// (all_accepted / pending / declined + counts). Capped at 4 concurrent
// requests so we don't hammer the API on first page load. Cache survives
// page navigation in-session so flipping back to page 1 is instant.
const consentCache = reactive({});   // { [productId]: { overall, counts, total } }
const consentInFlight = new Set();   // productIds currently being fetched
const CONSENT_CONCURRENCY = 4;
const consentQueue = [];             // pending productIds

const aggregateConsent = (items) => {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return { overall: 'empty', total: 0, counts: { accepted: 0, pending: 0, declined: 0 } };
  const counts = { accepted: 0, pending: 0, declined: 0 };
  for (const t of list) {
    const s = String(t.status || t.decision || 'pending').toLowerCase();
    if (s.startsWith('accept')) counts.accepted++;
    // declined / rejected / rescinded / withdrawn / revoked → all count
    // as a negative consent outcome (rendered red on the grid).
    else if (s.startsWith('declin') || s.startsWith('reject')
          || s.startsWith('rescind') || s.startsWith('withdraw') || s.startsWith('revoke')) counts.declined++;
    else counts.pending++;
  }
  let overall = 'accepted';
  if (counts.declined)     overall = 'declined';
  else if (counts.pending) overall = 'pending';
  return { overall, total: list.length, counts };
};

// Persisted-cache adapter: first look in the reactive in-memory hot cache,
// then fall back to localStorage (fresh entries only — older than the TTL
// are treated as misses so we re-fetch). Writing always goes through to
// both stores so a tab close keeps the warm cache around.
const tryConsentFromCache = id => {
  if (consentCache[id]) return true;
  const hit = cacheRead(consentCacheKey(id));
  if (!hit) return false;
  const age = Date.now() - hit.ts;
  if (age >= CONSENT_TTL_MS) return false;
  consentCache[id] = hit.data;
  return true;
};
const persistConsent = (id, payload) => {
  consentCache[id] = payload;
  cacheWrite(consentCacheKey(id), payload);
};

const drainConsentQueue = async () => {
  while (consentQueue.length && consentInFlight.size < CONSENT_CONCURRENCY) {
    const id = consentQueue.shift();
    if (consentCache[id] || consentInFlight.has(id)) continue;
    consentInFlight.add(id);
    FangateAPI.listProductConsentTags(id)
      .then(items => { persistConsent(id, aggregateConsent(items)); })
      .catch(() => {
        // Don't persist "unknown" to localStorage — we want a real retry
        // on next page load, not a sticky misleading dot colour.
        consentCache[id] = { overall: 'unknown', total: 0, counts: { accepted: 0, pending: 0, declined: 0 } };
      })
      .finally(() => {
        consentInFlight.delete(id);
        if (consentQueue.length) drainConsentQueue();
      });
  }
};

const enqueueConsentFetches = () => {
  for (const p of pagedProducts.value) {
    if (!p?.is_should_consent) continue;
    if (tryConsentFromCache(p.id)) continue;
    if (consentInFlight.has(p.id)) continue;
    if (!consentQueue.includes(p.id)) consentQueue.push(p.id);
  }
  drainConsentQueue();
};

// Trigger whenever the visible page slice changes (page nav OR filter/sort
// re-shuffles which items land on page 1). Settled cache entries are reused
// so re-rendering is free.
watch(pagedProducts, enqueueConsentFetches, { immediate: true });

// Currency stays EUR by default. The summary endpoint (which carries
// currency_code) was 403'ing for restricted tokens, so we no longer call it
// here. Re-introduce a silent ping later if non-EUR creators show up.
const currency = ref('EUR');

// Fangate stores prices as integer cents (e.g. 4400 = €44.00).
const centsToEuro = n => (typeof n === 'number' ? n / 100 : n);
const normalizeProduct = p => ({
  ...p,
  price: centsToEuro(p.price),
  total_earnings: centsToEuro(p.total_earnings),
});

const resetStates = () => {
  fetchError.value = '';
  permissionDenied.value = false;
};

// ----- Content folders (loaded from /api/content-folders) ------------------
// Per-creator folder taxonomy. Drives the detail-view folder picker so the
// dropdown shows ONLY folders Fangate actually knows — not names we infer
// from product rows. Reloaded whenever the inbox/account changes.
const apiFolders = ref([]);   // [{ id, name, ... }]
const loadFolders = async ({ force = false } = {}) => {
  if (!props.account?.id) { apiFolders.value = []; return; }
  try {
    if (force) cacheInvalidate(foldersCacheKey());
    const list = await cacheWrap(
      foldersCacheKey(),
      FOLDERS_TTL_MS,
      () => FangateAPI.listContentFolders({ refresh: force }),
      { force },
    );
    apiFolders.value = Array.isArray(list) ? list : [];
  } catch (_) {
    // 403 (token missing ability) → silent. Folder picker just shows empty.
    apiFolders.value = [];
  }
};
// Convenience: name lookup for products whose `folder` field is null but
// whose `folder_id` is set (Fangate behaviour varies — keep it defensive).
const folderNameOf = folderId => {
  if (folderId == null) return '';
  const f = apiFolders.value.find(x => Number(x.id) === Number(folderId));
  return f?.name || '';
};

// localStorage variant-link map (per-inbox). Same key the side panel writes
// to when the user creates a clone via the Edit-Price → New Link flow.
const variantMapKey = id => `fangate_variant_links_${id}`;
const loadVariantMap = () => {
  const id = props.account?.id;
  if (!id) return {};
  try { return JSON.parse(localStorage.getItem(variantMapKey(id)) || '{}'); }
  catch (_) { return {}; }
};

// ----- Cache TTLs (centralised so we can tune one place) ------------------
// TTLs match the server-side proxy cache (fangate_proxy_controller CACHE_TTLS)
// so both layers agree. localStorage is the per-tab layer; the proxy's Redis
// is the shared layer. The Reload/refresh buttons force-refresh BOTH.
const PRODUCTS_TTL_MS   = 2 * 60 * 1000;   // 2 min — vault content
const COLLECTION_TTL_MS = 2 * 60 * 1000;   // 2 min
const WALLET_TTL_MS     = 8 * 60 * 1000;   // 8 min — money panel (SWR shows instantly)
const FOLDERS_TTL_MS    = 2 * 60 * 1000;   // 2 min
const CONSENT_TTL_MS    = 2 * 60 * 1000;   // 2 min — per-product consent aggregate

const productsCacheKey   = () => `products:${props.account?.id}`;
const collectionCacheKey = () => `collection:${props.account?.id}`;
const walletCacheKey     = () => `wallet:${props.account?.id}`;
const foldersCacheKey    = () => `folders:${props.account?.id}`;
const consentCacheKey    = (pid) => `consent:${props.account?.id}:${pid}`;

// Hydrates the products grid + variant clusters from a raw response list.
// Pulled out of loadProducts so cache hits and live fetches share the same
// post-processing path.
const hydrateProductsFromList = (list) => {
  const normalized = Array.isArray(list) ? list.map(normalizeProduct) : [];
  const clusters = groupProductsByMedia(normalized, { variantMap: loadVariantMap() });
  Object.keys(variantsByPrimary).forEach(k => delete variantsByPrimary[k]);
  products.value = clusters.map(({ primary, variants }) => {
    if (variants.length > 1) variantsByPrimary[primary.id] = variants;
    return primary;
  });
};

// Cache-first products load. `force: true` (Reload button, post-upload
// refresh) bypasses the TTL gate AND invalidates the cache so the next
// background call sees the new state.
const loadProducts = async ({ force = false } = {}) => {
  if (!props.account?.id) return;

  // FAST PATH — a FRESH cache hit hydrates SYNCHRONOUSLY: no spinner, no grid
  // teardown. Switching back to an account (or between accounts whose data is
  // still cached) then feels instant instead of flashing a reload every time.
  // Only a real miss or an explicit `force` shows the loading state + fetches.
  // This also collapses the double-trigger (activeModule + account watchers
  // both call refreshActive on switch): two synchronous hydrates batch into a
  // single render, so there's no visible re-render either.
  if (!force) {
    const hit = cacheCheck(productsCacheKey(), PRODUCTS_TTL_MS);
    if (hit?.isFresh) {
      const cachedList = hit.data?.data ?? hit.data?.payload ?? hit.data ?? [];
      resetStates();
      hydrateProductsFromList(cachedList);
      isLoading.value = false;
      return;
    }
  }

  isLoading.value = true; resetStates();
  try {
    if (force) cacheInvalidate(productsCacheKey());
    const resp = await cacheWrap(
      productsCacheKey(),
      PRODUCTS_TTL_MS,
      () => FangateAPI.listProducts({ page: 1, limit: 50, refresh: force }),
      { force },
    );
    const list = resp?.data ?? resp?.payload ?? resp ?? [];
    hydrateProductsFromList(list);
  } catch (e) {
    if (is403(e)) permissionDenied.value = true;
    else fetchError.value = e?.message || 'Failed to load products';
    products.value = [];
    Object.keys(variantsByPrimary).forEach(k => delete variantsByPrimary[k]);
  } finally {
    isLoading.value = false;
  }
};

// Helpers for the grid card. Both safe to call when there are no variants —
// they just return zero/null.
const variantCountOf = primaryId =>
  (variantsByPrimary[primaryId] || []).length;
const priceRangeOf = primaryId => {
  const list = variantsByPrimary[primaryId];
  if (!list || list.length < 2) return null;
  const prices = list.map(v => Number(v.price) || 0);
  return { min: Math.min(...prices), max: Math.max(...prices) };
};

// Wallet: stale-while-revalidate. Show whatever we have INSTANTLY (even if
// stale), then background-refresh and swap when the new payload lands.
// Eliminates the "blank spinner → balance pops in" flash when the user
// flips between Library and Wallet.
const loadWallet = async ({ force = false } = {}) => {
  if (!props.account?.id) return;
  resetStates();
  if (force) cacheInvalidate(walletCacheKey());

  const swr = cacheSWR(
    walletCacheKey(),
    WALLET_TTL_MS,
    () => FangateAPI.getWallet({ refresh: force }),
    (data, meta) => {
      if (data) wallet.value = data;
      if (meta?.error) {
        if (is403(meta.error)) permissionDenied.value = true;
        else fetchError.value = meta.error?.message || 'Failed to load wallet';
      }
    },
  );

  if (swr.cached) {
    wallet.value = swr.cached;
    // Don't block the UI on the revalidation — let the background fetch
    // update wallet.value when it settles. Only spin when we had nothing.
  } else {
    isLoading.value = true;
    try { await swr.promise; }
    finally { isLoading.value = false; }
  }
};

// Load the creator's public-collection products.
// Same shape as listProducts; folder/variant grouping NOT applied here —
// the collection page is a flat curated view (collections can include
// individual variants on purpose).
const loadCollection = async ({ force = false } = {}) => {
  if (!props.account?.id) return;
  isLoadingCollection.value = true; resetStates();
  try {
    if (force) cacheInvalidate(collectionCacheKey());
    const resp = await cacheWrap(
      collectionCacheKey(),
      COLLECTION_TTL_MS,
      () => FangateAPI.getProductsCollection({ refresh: force }),
      { force },
    );
    const list = resp?.data ?? resp ?? [];
    collectionProducts.value = Array.isArray(list) ? list.map(normalizeProduct) : [];
  } catch (e) {
    if (is403(e)) permissionDenied.value = true;
    else fetchError.value = e?.message || 'Failed to load collection';
    collectionProducts.value = [];
  } finally {
    isLoadingCollection.value = false;
  }
};

// Remove a product from the public collection. Fangate's endpoint is a
// TOGGLE — calling once on an "in" product removes it. We optimistically
// drop the card and roll back on error.
const removeFromCollection = async product => {
  if (!product?.id) return;
  const before = collectionProducts.value;
  collectionProducts.value = before.filter(p => p.id !== product.id);
  try {
    await FangateAPI.toggleProductCollection(product.id);
  } catch (e) {
    // Roll back the optimistic removal so the user sees the actual state.
    collectionProducts.value = before;
    fetchError.value = e?.message || 'Failed to update collection';
  }
};

// Re-fetch only what the current module needs.
const refreshActive = () => {
  switch (activeModule.value) {
    case 'content-library': loadProducts();   break;
    case 'collection':      loadCollection(); break;
    // Wallet also needs the product list for the "Top products" panel.
    case 'wallet':          loadWallet(); loadProducts(); break;
    // placeholders need no fetch
    default: break;
  }
};

watch(activeModule, refreshActive);

// Rebind token scope + refetch whenever the account prop changes.
// `immediate: true` is safe here because all referenced fns are declared above.
// Folders are loaded eagerly too so the detail-view picker is populated the
// moment the user opens any product, no extra round-trip.
//
// Restore full Vault UI snapshot for THIS account (module + library
// search/folder/sort/type-filters/moderation/page). First-visit accounts
// get a clean default state.
const restoreVaultUiState = id => {
  const s = readVaultUiState(id) || {};
  SUPPRESS_PERSIST.value = true;
  // Mobile always opens the vault at the module LANDING (don't restore a deep
  // module — that would show a module's content/401 on tap instead of the menu).
  activeModule.value     = props.isMobile ? null : (s.module ?? null);
  search.value           = s.search           ?? '';
  selectedFolder.value   = s.selectedFolder   ?? null;
  sortMode.value         = s.sortMode         ?? 'newest';
  moderationFilter.value = s.moderationFilter ?? 'all';
  currentPage.value      = s.currentPage      ?? 1;
  activeFilters.type.clear();
  (s.typeFilters || []).forEach(t => activeFilters.type.add(t));
  // Let Vue flush all the reactive updates before re-enabling persistence
  // so the watchEffect doesn't fire 7 times mid-restore.
  nextTick(() => { SUPPRESS_PERSIST.value = false; });
};

watch(
  () => props.account?.id,
  id => {
    restoreVaultUiState(id);
    bindAccount(id);
    refreshActive();
    loadFolders();
  },
  { immediate: true }
);

// Persist the full UI snapshot whenever any tracked piece changes. Bundles
// 7 watchers into one watchEffect — Vue tracks the reactive reads inline.
// `Array.from(activeFilters.type)` forces a Set-content read so add/delete
// of type filters also triggers the effect.
watchEffect(() => {
  const id = props.account?.id;
  if (!id || SUPPRESS_PERSIST.value) return;

  const snapshot = {
    module:           activeModule.value,
    search:           search.value,
    selectedFolder:   selectedFolder.value,
    sortMode:         sortMode.value,
    typeFilters:      Array.from(activeFilters.type),
    moderationFilter: moderationFilter.value,
    currentPage:      currentPage.value,
  };
  try { localStorage.setItem(vaultUiStateKey(id), JSON.stringify(snapshot)); }
  catch (_) { /* quota / private mode — fail silent, persistence is opt-in */ }
});

// ---- Helpers ---------------------------------------------------------------
// Locale-aware money — same approach as FangatePanel.formatMoney
const localeForCurrency = code => ({
  EUR: 'de-DE', USD: 'en-US', GBP: 'en-GB', CHF: 'de-CH',
  CAD: 'en-CA', AUD: 'en-AU',
}[code] || 'en-US');

const moneyFormatter = computed(() => {
  const code = (currency.value || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat(localeForCurrency(code), {
      style: 'currency', currency: code,
    });
  } catch (_) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  }
});
const formatMoney = n => {
  if (n == null || isNaN(n)) return '—';
  if (n === 0) return 'Free';
  return moneyFormatter.value
    .format(Number(n))
    .replace(/([$£¥])(\d)/, '$1 $2');
};

// Wallet/balance amounts: always render as proper currency. Unlike product
// prices, a 0 € pending balance is real information ("nothing pending"), not
// "free" — so skip the "Free" short-circuit here.
const formatBalance = n => {
  if (n == null || isNaN(n)) return '—';
  return moneyFormatter.value
    .format(Number(n))
    .replace(/([$£¥])(\d)/, '$1 $2');
};

const formatRelTime = ts => {
  if (!ts) return '';
  // Fangate sends created_at as an ISO string (e.g. 2026-06-03T11:55:03Z);
  // some other callers pass a Unix timestamp. Handle both — Number() on an ISO
  // string is NaN, which is what produced the "NaNd ago" bug.
  let ms;
  if (typeof ts === 'number') ms = ts < 1e12 ? ts * 1000 : ts;  // seconds vs ms
  else                        ms = Date.parse(ts);
  if (!Number.isFinite(ms)) return '';
  const diff = Math.floor((Date.now() - ms) / 1000);
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return Math.floor(diff / 60)   + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  return Math.floor(diff / 86400) + 'd ago';
};

const accountInitials = computed(() => {
  const n = (props.account?.name || '?').trim();
  const p = n.split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '?';
});

// ---- Module sidebar interaction --------------------------------------------
// Upload Content is an ACTION, not a sub-page — clicking it opens the upload
// modal as an overlay. The current `activeModule` (typically Content Library)
// stays selected behind it; when the modal closes, the user lands back where
// they were and sees the new product if upload succeeded.
const uploadModalOpen = ref(false);
const selectModule = id => {
  if (id === 'upload') {
    uploadModalOpen.value = true;
    return;
  }
  // If the user clicks the SAME module they're already in while the detail
  // view is open, the activeModule watcher won't fire (value didn't change),
  // so the detail stays stuck open. Treat re-click as "drop me back to the
  // module's overview" — auto-save dirty edits first, then close detail.
  if (activeModule.value === id && detail.product) {
    saveAndCloseDetail();
    return;
  }
  activeModule.value = id;
};
// After a successful upload from the Vault: refetch the products list so the
// new product shows up immediately in the Content Library grid.
// New upload → blow the products cache so the next mount sees the new
// product even without a manual reload. Same for the toolbar's @refresh
// emit (FangateContentToolbar's icon refresh button still works) and the
// Collection's manual refresh button.
const onVaultUploaded = () => {
  cacheInvalidate(productsCacheKey());
  // The Collection list might also include the new product if it's flagged
  // in_collection on upload, so flush that cache too.
  cacheInvalidate(collectionCacheKey());
  loadProducts({ force: true });
};

// Top "Back" button is context-sensitive:
//   • If a product detail page is open → save (if dirty) + close detail
//   • Otherwise → exit the Vault (back to the chat list)
// Single-button-takes-you-one-level-up beats having two competing back
// affordances on the same view.
const goBack = async () => {
  if (detail.product) {
    await saveAndCloseDetail();
    return;
  }
  // Mobile single-pane: step back from a module to the module landing first,
  // instead of closing the whole vault.
  if (props.isMobile && activeModule.value) {
    activeModule.value = null;
    return;
  }
  emit('close');
};

// ============================================================================
// Product detail / inline editor (Content Library)
// ============================================================================
// When the user clicks a card in the Content Library grid, the same content
// pane swaps to an editable detail view for that product (prototype style:
// no modal — the grid replaces itself). Form fields mirror the Fangate
// product payload. Save calls FangateAPI.updateProduct (PATCH /products/{id}),
// then re-renders the grid with the merged result.
const detail = reactive({
  product: null,           // the product being viewed/edited (null = show grid)
  // cluster snapshot — siblings of `product` if it's part of a multi-clone
  // cluster (always includes `product` itself). Used to drive the variant
  // dropdown so the user can switch between price links without leaving
  // the detail view. Length 1 = no dropdown.
  cluster: [],
  initialPrice: 0,         // captured at open — drives "did the price change?"
  initialInCollection: false,  // captured at open — drives in_collection toggle call
  // editable form fields
  title:           '',
  publicDesc:      '',
  internalNotes:   '',
  price:           '',     // string for v-model number input
  folder:          '',     // folder NAME (string) — display label only
  folderId:        null,   // folder ID (number|null) — what Fangate stores
  initialFolderId: null,   // captured at open — drives "did the folder change?"
  flags: {
    avs18:        false,    // is_adult_content
    agecheck:     false,    // is_verif_age (buyer age check)
    download:     true,     // is_downloadable
    collection:   false,    // in_collection
    shouldConsent:false,    // is_should_consent (separate from tag list)
  },
  // lifecycle
  saving: false,
  error:  '',
});

// ============================================================================
// Co-creator consent tags (third-party consent flow)
// ============================================================================
// Fangate's model: instead of a yes/no consent flag, you tag specific other
// Fangate creators on your product. Each tag has its own decision lifecycle
// (pending → accepted/declined). Loaded lazily when the detail view opens.
const coTags = reactive({
  items: [],          // existing tags on the product
  loading: false,
  error:   '',
  // inline adder UI state — search-then-pick
  addMode:    false,
  addText:    '',        // search query (v-model on input)
  addSaving:  false,
  addError:   '',
  searching:  false,
  searchError:'',        // surface why /creators/search failed (was silent)
  results:    [],        // matches from /creators/search
  picked:     null,      // selected creator from results
});

let searchTimer = null;
const doSearchCreators = async () => {
  const q = String(coTags.addText || '').trim();
  if (q.length < 2) {
    coTags.results = []; coTags.searching = false; coTags.searchError = ''; return;
  }
  coTags.searching = true;
  coTags.searchError = '';
  try {
    const results = await FangateAPI.searchCreators(q);
    // eslint-disable-next-line no-console
    console.debug('[fangate] creators/search', q, '→', results);
    coTags.results = Array.isArray(results) ? results : [];
  } catch (e) {
    // Surface the real error instead of swallowing — empty result + a red
    // banner was indistinguishable from "no matches".
    coTags.results = [];
    coTags.searchError = e?.message || 'Search failed';
    // eslint-disable-next-line no-console
    console.warn('[fangate] creators/search failed:', e);
  } finally {
    coTags.searching = false;
  }
};
const onSearchInput = () => {
  // Typing invalidates any previously picked creator until user picks again.
  coTags.picked = null;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(doSearchCreators, 220);
};
const pickCreator = c => {
  coTags.picked  = c;
  // Show the picked creator's label in the input so the user sees the
  // commitment before clicking Tag.
  coTags.addText = creatorLabel(c);
  coTags.results = [];
};
// Friendly display name for a search result row. Per docs, creator objects
// expose display_name / first_name / last_name / email.
const creatorLabel = c => {
  if (!c) return 'Unknown';
  if (c.display_name) return c.display_name;
  const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return full || c.email || `Creator #${c.id ?? '?'}`;
};

// Two-letter initials from a display string — for avatar circles when the
// creator object has no avatar URL.
const getInitials = label => {
  const s = String(label || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
};

const loadConsentTags = async () => {
  if (!detail.product?.id) return;
  coTags.loading = true; coTags.error = '';
  try {
    const tags = await FangateAPI.listProductConsentTags(detail.product.id);
    coTags.items = Array.isArray(tags) ? tags : [];
  } catch (e) {
    // Silently swallow 404s here — the GET path is still being verified
    // against Fangate's real API. A red banner on every item-open is
    // noisier than just rendering the empty state. POST errors (when the
    // user actively tries to tag someone) still surface via coTags.addError.
    const msg = e?.message || '';
    if (!msg.includes('HTTP 404')) coTags.error = msg || 'Failed to load co-creators';
    coTags.items = [];
  } finally {
    coTags.loading = false;
  }
};

const openCoAdder = () => {
  coTags.addMode     = true;
  coTags.addText     = '';
  coTags.addError    = '';
  coTags.searchError = '';
  coTags.results     = [];
  coTags.picked      = null;
  coTags.searching   = false;
};
const cancelCoAdder = () => {
  coTags.addMode     = false;
  coTags.addText     = '';
  coTags.addError    = '';
  coTags.searchError = '';
  coTags.results     = [];
  coTags.picked      = null;
  if (searchTimer) { clearTimeout(searchTimer); searchTimer = null; }
};

const submitCoCreator = async () => {
  if (!detail.product?.id) return;
  if (!coTags.picked?.id) {
    coTags.addError = 'Pick a creator from the search results first.';
    return;
  }
  coTags.addSaving = true; coTags.addError = '';
  try {
    await FangateAPI.addProductConsentTag({
      productId:       detail.product.id,
      taggedCreatorId: coTags.picked.id,
      date:            new Date().toISOString().slice(0, 10),
    });
    await loadConsentTags();
    cancelCoAdder();
  } catch (e) {
    coTags.addError = e?.message || 'Failed to tag co-creator';
  } finally {
    coTags.addSaving = false;
  }
};

// One-shot tag-from-result-row. The new UX is search-always-visible: each
// search result has its own "Tag creator" button, no separate Pick→Confirm
// step. taggingId tracks which row's button is currently saving so we can
// disable just that one row's button (not the whole list).
const taggingId = ref(null);
const tagCreatorFromResult = async creator => {
  if (!detail.product?.id || !creator?.id) return;
  taggingId.value = creator.id;
  coTags.addError = '';
  try {
    await FangateAPI.addProductConsentTag({
      productId:       detail.product.id,
      taggedCreatorId: creator.id,
      date:            new Date().toISOString().slice(0, 10),
    });
    await loadConsentTags();
    // Clear the search after a successful tag so the user can search again.
    coTags.addText = '';
    coTags.results = [];
  } catch (e) {
    coTags.addError = e?.message || 'Failed to tag co-creator';
  } finally {
    taggingId.value = null;
  }
};

// Email-invite fallback — DISABLED 2026-06-03 (task #97).
//
// Empirical finding: Fangate accepts the POST but does NOT actually send
// the consent-request email to the tagged user yet. The tag stays as
// "pending email-invite" on their side forever and the recipient never
// gets a notification. Showing the button gives users a false sense of
// progress — they think the invite went out, then wonder why no creator
// shows up.
//
// Re-enable once Fangate ships their `email` mode delivery: set the
// computed back to the previous logic (isEmailLike(q) && !searching &&
// !results.length && !taggingId.value). Tracked on roadmap as #97.
const isEmailLike = s => typeof s === 'string' && /^\S+@\S+\.\S+$/.test(s.trim());
const showEmailInvite = computed(() => false);
const invitingEmail = ref(false);
const inviteByEmail = async () => {
  const email = String(coTags.addText || '').trim();
  if (!detail.product?.id || !isEmailLike(email)) return;
  invitingEmail.value = true;
  coTags.addError = '';
  try {
    await FangateAPI.addProductConsentTag({
      productId: detail.product.id,
      email,
      date:      new Date().toISOString().slice(0, 10),
    });
    await loadConsentTags();
    coTags.addText = '';
    coTags.results = [];
  } catch (e) {
    coTags.addError = e?.message || 'Failed to send invite';
  } finally {
    invitingEmail.value = false;
  }
};

// Note: Fangate has no documented endpoint to delete a consent tag from
// the product-owner side. The tag is removed only when the tagged
// creator declines it. So no removeCoCreator() function — the × on
// chips was dropped from the UI.

// ============================================================================
// Moderation status — sourced from media[].veriff_status on the full product
// (GET /products/{id}). The list-products endpoint doesn't always include
// media; we refetch on open to make sure we have it.
// ============================================================================
const fullProduct = ref(null);
// Tri-state so the UI can distinguish "still loading" from "fetch failed /
// no media on the resource" — the original code conflated both into null,
// which left the Moderation pill stuck on "Loading…" forever when the
// fetch errored (common: 403 right after product creation).
const fullProductLoaded = ref(false);

const loadFullProduct = async () => {
  fullProductLoaded.value = false;
  if (!detail.product?.id) { fullProduct.value = null; fullProductLoaded.value = true; return; }
  try {
    fullProduct.value = await FangateAPI.getProduct(detail.product.id);
    // eslint-disable-next-line no-console
    console.debug('[fangate] full product loaded', detail.product.id, fullProduct.value);
  } catch (e) {
    fullProduct.value = null;   // fallback to detail.product (from list) below
    // eslint-disable-next-line no-console
    console.debug('[fangate] getProduct failed for', detail.product.id, e?.message);
  } finally {
    fullProductLoaded.value = true;
  }
};

// Manual refresh — user-triggered via the refresh icon next to the status
// pills. Re-runs the full-product fetch (moderation) AND the consent-tag
// list so both sides of the status row update in one click. Useful after
// Fangate finishes a manual approval / consent recipient responds.
const refreshingStatus = ref(false);
const refreshStatus = async () => {
  if (!detail.product || refreshingStatus.value) return;
  refreshingStatus.value = true;
  try {
    await Promise.all([loadFullProduct(), loadConsentTags()]);
  } finally {
    refreshingStatus.value = false;
  }
};

// Media can live on EITHER the freshly-fetched full product OR the list-row
// `detail.product` (some Fangate list responses include media[], some don't).
// Falling through both means moderation still renders if /products/{id} 403s
// but the list payload had the field.
const mediaList = computed(() => {
  const a = fullProduct.value?.media;
  if (Array.isArray(a) && a.length) return a;
  const b = detail.product?.media;
  return Array.isArray(b) ? b : [];
});

// Some Fangate responses expose a single top-level moderation/removal string
// (e.g. `status: "AI Removal"` or `veriff_status: "AI Removal"`) without
// expanding the media array. We sniff every plausible field so the pill
// reflects what the Fangate dashboard shows.
const productLevelStatus = computed(() => {
  const p = fullProduct.value || detail.product || {};
  return p.veriff_status
      || p.moderation_status
      || p.status
      || p.removal_status
      || (p.is_removed ? 'AI Removal' : '')
      || '';
});

// Five documented Fangate veriff_status enum values (per
// https://docs.fangate.app/api-reference/products.html):
//   • "Unchecked"        → just uploaded, AI hasn't scanned yet
//   • "Approved"         → AI cleared
//   • "AI Removal"       → AI rejected; can still be overturned by a human
//   • "Manual Approval"  → human reviewer cleared
//   • "Manual Removal"   → human reviewer rejected (final)
// IMPORTANT ORDERING NOTES:
//   - "Manual Approval" / "Manual Removal" both start with "Manual" — the
//     explicit phrase matches MUST come before any generic "approv"/"removed"
//     fallback or they'd get tagged as the wrong sub-state.
//   - "AI Removal" check before the generic "removed" fallback so the
//     "(Temporarily) rejected" label sticks (AI removals can be overturned;
//     manual removals are final → just "Rejected").
const mapVeriffStatus = raw => {
  const s = String(raw || '').toLowerCase().trim();
  if (!s) return null;
  if (s.includes('manual removal'))               return 'manual_rem';
  if (s.includes('manual approval'))              return 'manual_appr';
  if (s.includes('ai removal'))                   return 'ai_removed';
  if (s === 'approved' || s.includes('approv'))   return 'approved';
  if (s === 'unchecked' || s.includes('unchecked')) return 'unchecked';
  // Defensive fallbacks for undocumented values (e.g. legacy "Manual review"
  // or other variants we encounter empirically).
  if (s.includes('removed') || s.includes('reject')) return 'ai_removed';
  if (s.includes('manual')  || s.includes('review')) return 'unchecked';
  if (s === 'pending' || s.includes('pending'))      return 'unchecked';
  return null;
};

// Priority order for worst-case aggregation across media[] + product-level
// status. Lower index = worse; first hit wins.
const VERIFF_PRIORITY = ['manual_rem', 'ai_removed', 'unchecked', 'manual_appr', 'approved'];

// Lightweight per-product moderation lookup for the Library grid cards —
// derives from whatever the list-row exposes (top-level fields + media[].
// veriff_status if present). Returns one of the five mapVeriffStatus keys
// or null when nothing is known.
const productModerationKey = (p) => {
  if (!p) return null;
  const seen = new Set();
  const top = mapVeriffStatus(
    p.veriff_status || p.moderation_status || p.status || p.removal_status
    || (p.is_removed ? 'AI Removal' : '')
  );
  if (top) seen.add(top);
  const media = Array.isArray(p.media) ? p.media : [];
  for (const m of media) {
    const k = mapVeriffStatus(m.veriff_status);
    if (k) seen.add(k);
  }
  if (!seen.size) return null;
  for (const k of VERIFF_PRIORITY) if (seen.has(k)) return k;
  return null;
};

// Per-product consent indicator for the Library grid. Returns:
//   • 'none'      — consent isn't required on this product
//   • 'loading'   — required + fetch is in-flight (no cache entry yet)
//   • 'accepted'  — all tagged co-creators accepted
//   • 'pending'   — at least one tag still awaiting response
//   • 'declined'  — at least one tagged co-creator rejected
//   • 'empty'     — required but no tags exist yet (creator hasn't added any)
//   • 'unknown'   — fetch failed (403, network, etc.)
// Only products with is_should_consent are queued for fetch (see
// enqueueConsentFetches), so we never pay for products that don't need it.
const productConsentKey = (p) => {
  if (!p) return 'none';
  if (!p.is_should_consent) return 'none';
  const cached = consentCache[p.id];
  if (!cached) return 'loading';
  return cached.overall;   // 'accepted' | 'pending' | 'declined' | 'empty' | 'unknown'
};

const consentGridLabel = key => ({
  none:     'No consent required',
  loading:  'Checking…',
  accepted: 'All accepted',
  pending:  'Pending',
  declined: 'Declined',
  empty:    'No tags',
  unknown:  '—',
}[key] || '—');

const consentGridCss = key => ({
  none:     'mute',
  loading:  'mute',
  accepted: 'approved',
  pending:  'pending',
  declined: 'removed',
  empty:    'pending',     // required but no tags = effectively pending
  unknown:  'unknown',
}[key] || 'mute');

// Aggregate moderation across media[] + top-level product status. Returns
// null when nothing is known yet so the pill can show "Status unavailable".
//   overall: one of the five Fangate enum keys (see mapVeriffStatus)
//   total:   number of media items
//   items:   the full media array (for the per-file breakdown row)
const moderationSummary = computed(() => {
  const list = mediaList.value;
  const topLevel = mapVeriffStatus(productLevelStatus.value);

  const seen = new Set();
  if (topLevel) seen.add(topLevel);
  for (const m of list) {
    const k = mapVeriffStatus(m.veriff_status);
    if (k) seen.add(k);
  }
  if (!seen.size) return null;

  // Worst-case wins per VERIFF_PRIORITY (manual_rem > ai_removed > unchecked
  // > manual_appr > approved). A top-level removal also overrides stale
  // per-media "Approved" entries.
  let overall = 'approved';
  for (const p of VERIFF_PRIORITY) {
    if (seen.has(p)) { overall = p; break; }
  }
  return { overall, total: list.length, items: list };
});

// Display label per documented Fangate state. AI Removal gets the
// "(Temporarily)" qualifier because those can be appealed/overturned by
// a human reviewer; Manual Removal is final ("Removed").
const moderationLabel = status => ({
  approved:    'Approved',
  manual_appr: 'Approved',
  unchecked:   'In review',
  ai_removed:  '(Temporarily) rejected',
  manual_rem:  'Removed',
  unknown:     'Status unavailable',
}[status] || 'Unknown');

// CSS pill class — collapses the 5 enum values onto our 3 colour buckets
// (green / yellow / red) since both approved variants are green and both
// removal variants are red.
const moderationCss = status => ({
  approved:    'approved',
  manual_appr: 'approved',
  unchecked:   'pending',
  ai_removed:  'removed',
  manual_rem:  'removed',
  unknown:     'unknown',
}[status] || 'unknown');

// ============================================================================
// Consent summary — derived from the consent-creator-tags list (Fangate does
// not expose a top-level consent_status field on Product, per swagger spec).
// Worst-case rolls up to overall:
//   • any declined → 'declined'
//   • else any pending → 'pending'
//   • else (all accepted) → 'accepted'
// Returns null when consent isn't required OR no tags exist yet.
// ============================================================================
const consentSummary = computed(() => {
  if (!detail.flags.shouldConsent) return null;
  const list = Array.isArray(coTags.items) ? coTags.items : [];
  if (!list.length) return { overall: 'empty', total: 0, counts: { accepted: 0, pending: 0, declined: 0 } };
  const counts = { accepted: 0, pending: 0, declined: 0 };
  for (const t of list) {
    const s = String(t.status || t.decision || 'pending').toLowerCase();
    if (s.startsWith('accept')) counts.accepted++;
    else if (s.startsWith('declin') || s.startsWith('reject')) counts.declined++;
    else counts.pending++;
  }
  let overall = 'accepted';
  if (counts.declined)     overall = 'declined';
  else if (counts.pending) overall = 'pending';
  return { overall, total: list.length, counts };
});

const consentLabel = status => ({
  accepted: 'All consents accepted',
  pending:  'Awaiting consent',
  declined: 'Consent declined',
  empty:    'No co-creators tagged yet',
}[status] || 'Unknown');

// Friendly label for a tag chip. Per docs the API returns each tag with a
// nested `tagged_user` object carrying display_name / first_name / last_name
// / email.
const displayCoTag = t => {
  const u = t.tagged_user || t.user || {};
  if (u.display_name) return u.display_name;
  const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return full || u.email || t.email || `User #${u.id || t.tagged_user_id || '?'}`;
};

// Decision pill: pending / accepted / declined → matches Fangate's lifecycle.
// rescinded / withdrawn / revoked are treated as declined for display so the
// roll-up colour stays consistent with aggregateConsent.
const decisionBadge = t => {
  const d = String(t.decision || t.status || 'pending').toLowerCase();
  if (d.startsWith('accept')) return { text: 'accepted', cls: 'accepted' };
  if (d.startsWith('declin') || d.startsWith('reject')
   || d.startsWith('rescind') || d.startsWith('withdraw') || d.startsWith('revoke')) {
    return { text: 'declined', cls: 'declined' };
  }
  return { text: 'pending', cls: 'pending' };
};

// opts.keepCluster: stay in the same variant-cluster session (used by
// switchVariant — without this, opening a non-primary clone would shrink
// the dropdown to just that clone).
const openProductDetail = (product, opts = {}) => {
  // Log the raw product payload so we can confirm what moderation/status
  // fields Fangate actually returns (the documented schema is incomplete).
  // eslint-disable-next-line no-console
  console.debug('[fangate] open product detail (from list)', product?.id, product);
  detail.product       = product;
  if (!opts.keepCluster) {
    const siblings = variantsByPrimary[product.id];
    detail.cluster = (siblings && siblings.length > 1) ? siblings : [product];
  }
  detail.title         = product.title         || '';
  detail.publicDesc    = product.public_description || product.description || '';
  detail.internalNotes = product.internal_notes || '';
  detail.price         = product.price != null ? String(product.price) : '';
  detail.initialPrice  = Number(product.price) || 0;
  // Folder: track BOTH the id (canonical, sent to Fangate) and the name
  // (display only). product.folder can be: null, string name, or
  // {id, name, ...} object — productFolderName handles all three.
  detail.folderId        = product.folder_id ?? product.folder?.id ?? null;
  detail.initialFolderId = detail.folderId;
  detail.folder          = productFolderName(product) || folderNameOf(detail.folderId);
  detail.flags.avs18         = !!(product.is_adult_content);
  detail.flags.agecheck      = !!(product.is_verif_age);
  detail.flags.download      = product.is_downloadable !== false;  // default on
  detail.flags.collection    = !!(product.in_collection);
  detail.initialInCollection = detail.flags.collection;
  detail.flags.shouldConsent = !!(product.is_should_consent);
  detail.saving = false;
  detail.error  = '';
  // Reset + auto-load co-creator consent tags for this product
  coTags.items   = [];
  coTags.error   = '';
  coTags.addMode = false;
  coTags.addText = '';
  loadConsentTags();
  // Also refetch the full product so we have media[] for the moderation
  // status block (list-products doesn't always include media).
  fullProduct.value = null;
  loadFullProduct();
  // Drop any inherited focus so no input gets a blinking cursor on open.
  // After the click on the product card, focus would otherwise sometimes
  // land in the number-typed Price input.
  nextTick(() => { document.activeElement?.blur?.(); });
};
const closeDetail = () => {
  detail.product = null;
  detail.cluster = [];
  editingTitle.value = false;
};

// Inline title editor in the head card. Click the pencil → swap the bold
// title for an input; commit on Enter/blur. Replaces the old standalone
// "TITLE" form row so the bold heading is the editable surface.
const editingTitle = ref(false);
const titleInputRef = ref(null);
const startEditTitle = async () => {
  editingTitle.value = true;
  await nextTick();
  titleInputRef.value?.focus?.();
  titleInputRef.value?.select?.();
};
const commitEditTitle = () => { editingTitle.value = false; };

// Has the user modified the form vs. the snapshot taken in openProductDetail?
// Used to decide whether to fire a save when navigating away (Back, sidebar
// Library click, etc.). Compares only the fields the detail form exposes.
const detailDirty = computed(() => {
  const p = detail.product;
  if (!p) return false;
  const priceNow = parseFloat(String(detail.price || '').trim()) || 0;
  if (Math.abs(priceNow - Number(detail.initialPrice || 0)) > 0.001) return true;
  if (Number(detail.folderId ?? -1) !== Number(detail.initialFolderId ?? -1)) return true;
  if (detail.flags.collection !== detail.initialInCollection) return true;
  if ((detail.title || '')         !== (p.title || ''))                        return true;
  if ((detail.publicDesc || '')    !== (p.public_description || p.description || '')) return true;
  if ((detail.internalNotes || '') !== (p.internal_notes || ''))               return true;
  if (detail.flags.avs18         !== !!p.is_adult_content)    return true;
  if (detail.flags.agecheck      !== !!p.is_verif_age)        return true;
  if (detail.flags.download      !== (p.is_downloadable !== false)) return true;
  if (detail.flags.shouldConsent !== !!p.is_should_consent)   return true;
  return false;
});

// Save (if dirty) then drop back to the grid. Used by every "leave detail"
// affordance — top Back button, inline "Back to library", and module switch.
// On save failure: stay on the detail page so the user can see the error and
// retry; closeDetail() is only called on success (or when nothing changed).
const saveAndCloseDetail = async () => {
  if (!detail.product) return;
  if (detailDirty.value) {
    await saveProductDetail();
    if (detail.error) return;   // keep the detail open so the error is visible
  }
  closeDetail();
};

// Switch the detail view to a different variant of the same cluster without
// closing/reopening (preserves the cluster snapshot powering the dropdown).
const switchVariant = variantId => {
  const v = (detail.cluster || []).find(x => String(x.id) === String(variantId));
  if (v && v.id !== detail.product?.id) openProductDetail(v, { keepCluster: true });
};

// =============================================================================
// Edit-Price modal — reuse the side-panel component for "Change price" /
// "+ New link with new price". The Vault detail's own form handles
// metadata + price for the CURRENT variant; this modal is specifically for
// creating new price variants of the same content.
// =============================================================================
const priceModalOpen = ref(false);
const openPriceModalForDetail = () => { priceModalOpen.value = true; };

// "Change price" result: update the active variant in cluster + grid.
// Mirrors what saveProductDetail's price branch already does, but routed
// through the modal's own PATCH /price call (avoids duplicate save).
const onPriceModalChanged = updated => {
  if (!detail.product) return;
  const merged = { ...detail.product, ...updated };
  detail.product = merged;
  detail.price = String(merged.price ?? '');
  detail.initialPrice = Number(merged.price) || 0;
  const ci = (detail.cluster || []).findIndex(x => x.id === merged.id);
  if (ci > -1) detail.cluster[ci] = merged;
  const pi = products.value.findIndex(x => x.id === merged.id);
  if (pi > -1) {
    const next = [...products.value];
    next[pi] = { ...next[pi], ...merged };
    products.value = next;
  }
};

// "+ New link" result: append the new product to the cluster, record the
// variant link in localStorage so it persists across reload, and switch
// the detail view to the new variant so the user can edit its metadata.
const variantMapKeyForVault = id => `fangate_variant_links_${id}`;
const recordVariantLinkInVault = (sourceId, variantId) => {
  if (!props.account?.id || !sourceId || !variantId) return;
  try {
    const key = variantMapKeyForVault(props.account.id);
    const m = JSON.parse(localStorage.getItem(key) || '{}');
    const list = m[sourceId] || [];
    if (!list.includes(variantId)) list.push(variantId);
    m[sourceId] = list;
    localStorage.setItem(key, JSON.stringify(m));
  } catch (_) {}
};
const onPriceModalNewLink = (sourceVariantId, newVariant) => {
  if (!detail.product) return;
  // The variant comes back with {id, title, price, url} from the modal —
  // promote to a product-shaped object the rest of the Vault expects.
  const productShape = {
    id:    newVariant.id,
    title: newVariant.title,
    price: newVariant.price,
    link:  newVariant.url,
    type:  detail.product.type,
    items: detail.product.items,
    // Copy media reference so media-path grouping still clusters it on reload.
    media: detail.product.media,
  };
  detail.cluster = [...detail.cluster, productShape];
  recordVariantLinkInVault(sourceVariantId, newVariant.id);
  // Switch the detail view to the newly created variant so the user can
  // immediately set its title/description.
  openProductDetail(productShape, { keepCluster: true });
};
const toggleDetailFlag = key => {
  detail.flags[key] = !detail.flags[key];
  // Age check only meaningful when AVS is on (matches upload modal rule).
  if (key === 'avs18' && !detail.flags.avs18) detail.flags.agecheck = false;
};

const saveProductDetail = async () => {
  const p = detail.product;
  if (!p) return;
  const rawPrice = String(detail.price || '').trim();
  const priceEur = rawPrice === '' ? 0 : parseFloat(rawPrice);
  if (rawPrice === '' || isNaN(priceEur) || priceEur < 5 || priceEur > 224) {
    detail.error = 'Price must be between €5 and €224.';
    return;
  }
  detail.saving = true;
  detail.error  = '';

  // Fangate splits the editable surface across FOUR endpoints:
  //   • PATCH /products/{id}             → title, descriptions, flags (NOT in_collection)
  //   • PATCH /products/{id}/price       → price (only)
  //   • PATCH /products/{id}/folder      → folder_id (only)
  //   • POST  /products/{id}/collection  → toggle in/out of collection
  // The /products endpoint silently ignores `in_collection`, `folder`, and
  // `folder_id` fields — those need their dedicated routes.
  const metaPayload = {
    title:               detail.title || p.title,
    public_description:  detail.publicDesc,
    internal_notes:      detail.internalNotes,
    is_adult_content:    detail.flags.avs18,
    is_verif_age:        detail.flags.agecheck,
    is_downloadable:     detail.flags.download,
    is_should_consent:   detail.flags.shouldConsent,
  };
  const priceCents         = Math.round(priceEur * 100);
  const priceChanged       = Math.abs(priceEur - Number(detail.initialPrice || 0)) > 0.001;
  const folderChanged      =
    Number(detail.folderId ?? -1) !== Number(detail.initialFolderId ?? -1);
  const collectionChanged  = detail.flags.collection !== detail.initialInCollection;

  try {
    const meta = await FangateAPI.updateProduct(p.id, metaPayload);
    let priceResp = null;
    if (priceChanged) {
      priceResp = await FangateAPI.updateProductPrice(p.id, priceCents);
    }
    if (folderChanged) {
      // PATCH /products/{id}/folder — Fangate doesn't accept folder via the
      // products endpoint. detail.folderId === null clears the folder.
      await FangateAPI.updateProductFolder(p.id, detail.folderId);
    }
    if (collectionChanged) {
      // POST /products/{id}/collection is a TOGGLE — calling it flips the
      // membership. Since we only call when the flag actually changed, one
      // POST takes the product to the desired state.
      await FangateAPI.toggleProductCollection(p.id);
    }
    // Build the merged product. Price (and `link`) come from /price if we
    // hit it; everything else from /products metadata or our optimistic
    // payload.
    const merged = normalizeProduct({
      ...p,
      ...metaPayload,
      ...(meta || {}),
      price:         priceResp?.price ?? (priceChanged ? priceCents : p.price * 100),
      link:          priceResp?.link  ?? p.link,
      folder_id:     folderChanged     ? detail.folderId            : p.folder_id,
      folder:        folderChanged     ? folderNameOf(detail.folderId) : p.folder,
      in_collection: collectionChanged ? detail.flags.collection    : p.in_collection,
    });

    // Replace the product in the top-level list. If the saved product is
    // the cluster primary, swap it in place. If it's a non-primary clone,
    // we only need to update the cluster entry — the grid card stays on
    // the (unchanged) primary, but the price-range / dropdown will reflect
    // the new price next time the cluster is read.
    const primaryIdx = products.value.findIndex(x => x.id === p.id);
    if (primaryIdx > -1) {
      const next = [...products.value];
      next[primaryIdx] = { ...next[primaryIdx], ...merged };
      products.value = next;
    }
    // Update the cluster snapshot so the dropdown shows the new price too.
    const ci = (detail.cluster || []).findIndex(x => x.id === p.id);
    if (ci > -1) {
      detail.cluster[ci] = { ...detail.cluster[ci], ...merged };
    }
    // Stay on the detail page after Save — closing the detail forced the
    // user to re-navigate to keep editing. Refresh the "initial*" snapshots
    // so the next Save knows what's pristine vs. changed.
    detail.product         = merged;
    detail.initialPrice    = Number(merged.price) || 0;
    detail.initialFolderId = detail.folderId;
    detail.initialInCollection = detail.flags.collection;
  } catch (e) {
    detail.error = e?.message || 'Save failed.';
  } finally {
    detail.saving = false;
  }
};

// When the user switches modules (sidebar: Library / Collection / Wallet /
// Collaborations) while a product detail is open, auto-save dirty edits then
// drop the detail view. Matches the back-button behaviour so navigation never
// silently discards work.
watch(activeModule, () => { if (detail.product) saveAndCloseDetail(); });

// Folder picker mode: 'pick' = select from existing folders dropdown,
// 'new'  = type a new folder name. Flips when user clicks "+ New folder".
const folderMode = ref('pick');
const folderPickerOpen = ref(false);

// Folder options come from /api/content-folders (apiFolders) so the picker
// shows the same taxonomy Fangate sees — not names inferred from product
// rows. Sorted alphabetically for predictable display.
const folderOptions = computed(() =>
  [...(apiFolders.value || [])]
    .filter(f => f && f.name)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
);

// Pick an existing folder. Folder object carries the canonical {id, name}.
const setFolder = folder => {
  if (folder) {
    detail.folderId = Number(folder.id);
    detail.folder   = folder.name || '';
  } else {
    detail.folderId = null;
    detail.folder   = '';
  }
  folderPickerOpen.value = false;
};
const clearFolder = () => setFolder(null);

// "+ New folder" flow — creates the folder on Fangate via
// POST /content-folders, then selects it on this product.
const newFolderName = ref('');
const newFolderSaving = ref(false);
const startNewFolder = () => {
  folderMode.value = 'new';
  newFolderName.value = '';
  folderPickerOpen.value = false;
};
const cancelNewFolder = () => {
  folderMode.value = 'pick';
  newFolderName.value = '';
};
const confirmNewFolder = async () => {
  const name = String(newFolderName.value || '').trim();
  if (!name) return;
  newFolderSaving.value = true;
  try {
    const created = await FangateAPI.createContentFolder(name);
    if (created?.id) {
      apiFolders.value = [...apiFolders.value, created];
      setFolder(created);
    }
    folderMode.value = 'pick';
    newFolderName.value = '';
  } catch (e) {
    detail.error = e?.message || 'Failed to create folder.';
  } finally {
    newFolderSaving.value = false;
  }
};

// Build a sharable product URL — prefer the field Fangate returned on the
// product itself; fall back to FangateAPI.buildProductLink(id).
const productLink = computed(() => {
  if (!detail.product) return '';
  return detail.product.link
      || detail.product.url
      || FangateAPI.buildProductLink(detail.product.id);
});

const copiedFlash = ref(false);
const copyProductLink = async () => {
  const url = productLink.value;
  if (!url) return;
  try { await navigator.clipboard.writeText(url); }
  catch (_) {
    // Fallback for browsers without clipboard API permission
    const ta = document.createElement('textarea');
    ta.value = url;
    ta.style.cssText = 'position:fixed;top:-9999px;';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (_e) {}
    document.body.removeChild(ta);
  }
  copiedFlash.value = true;
  setTimeout(() => { copiedFlash.value = false; }, 1800);
};
const previewProductLink = () => {
  const url = productLink.value;
  if (url) window.open(url, '_blank', 'noopener');
};

// "Preview content" — opens the underlying file (media[0].source) in a new
// tab. Mirrors the verified fallback chain from FangatePanel#openContentFor
// and FangateItemEditModal#previewUrl: media[0].source → preview_url → url →
// product.preview → product.thumb. media[] lives at PRODUCT level (shared
// across variants), so no activeVariant lookup is needed here.
const productContentUrl = computed(() => {
  const p = detail.product;
  if (!p) return '';
  const media = Array.isArray(p.media) ? p.media : [];
  const first = media[0] || {};
  return first.source || first.preview_url || first.url || p.preview || p.thumb || '';
});
const previewProductContent = () => {
  const url = productContentUrl.value;
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
};

// Reset folder picker mode whenever a fresh product is opened.
watch(() => detail.product?.id, () => {
  folderMode.value = 'pick';
  folderPickerOpen.value = false;
});

// Close folder dropdown on outside click — same pattern as the rest of the
// fangate dropdowns. Click handlers on the button + menu itself use
// `@click.stop` so they don't trigger this.
const closeFolderPicker = () => { folderPickerOpen.value = false; };
onMounted(() => document.addEventListener('click', closeFolderPicker));
onUnmounted(() => document.removeEventListener('click', closeFolderPicker));
</script>

<template>
  <div class="fv-root" :class="{ 'fv-mod-open': activeModule }">

    <!-- ========== Top bar with breadcrumb / back ========== -->
    <header class="fv-top">
      <button class="fv-back" @click="goBack"
              :title="detail.product ? 'Back to Library' : 'Back to chats'">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2.2"
             stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Back
      </button>
      <div class="fv-breadcrumb">
        <span class="fv-bc-muted">Fangate /</span>
        <span class="fv-bc-name">{{ account.name }}</span>
      </div>
    </header>

    <!-- ========== Vault body: module sidebar + content ========== -->
    <div class="fv-body">

      <!-- ===== Module sidebar (left, fixed width) ===== -->
      <aside class="fv-modnav">
        <!-- Brand block -->
        <div class="fv-brand">
          <svg class="fv-brand-svg" viewBox="0 0 260 44" xmlns="http://www.w3.org/2000/svg"
               role="img" aria-label="FANGATE.">
            <defs>
              <linearGradient id="fv-text-grad" x1="0%" y1="50%" x2="100%" y2="50%">
                <stop offset="0%"   stop-color="#1a0a6b"/>
                <stop offset="40%"  stop-color="#3818b5"/>
                <stop offset="80%"  stop-color="#5b2ecf"/>
                <stop offset="100%" stop-color="#7a3ce0"/>
              </linearGradient>
            </defs>
            <text x="130" y="35" text-anchor="middle"
                  font-family="Inter, system-ui, sans-serif"
                  font-weight="900" font-size="40" letter-spacing="-2"
                  text-rendering="geometricPrecision" fill="url(#fv-text-grad)">FANGATE.</text>
          </svg>
          <div class="fv-welcome">Welcome, {{ account.name }}</div>
        </div>

        <!-- Module buttons -->
        <button
          v-for="m in MODULES" :key="m.id"
          class="fv-modbtn"
          :class="{
            'fv-modbtn--active':  activeModule === m.id,
            'fv-modbtn--primary': m.primary,
            'fv-modbtn--gap':     m.primary,
          }"
          @click="selectModule(m.id)"
        >
          <span class="fv-modbtn-icon">
            <!-- Upload -->
            <svg v-if="m.icon === 'upload'" width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <!-- Dashboard -->
            <svg v-else-if="m.icon === 'dashboard'" width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <rect x="3"  y="3"  width="7" height="7" rx="1"/>
              <rect x="14" y="3"  width="7" height="7" rx="1"/>
              <rect x="3"  y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
            <!-- Library -->
            <svg v-else-if="m.icon === 'library'" width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="4"  y1="4" x2="4"  y2="20"/>
              <line x1="8"  y1="4" x2="8"  y2="20"/>
              <line x1="12" y1="4" x2="12" y2="20"/>
              <line x1="16" y1="4" x2="16" y2="20"/>
              <line x1="20" y1="4" x2="20" y2="20"/>
            </svg>
            <!-- Folder -->
            <svg v-else-if="m.icon === 'folder'" width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg>
            <!-- Wallet -->
            <svg v-else-if="m.icon === 'wallet'" width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <rect x="2" y="6" width="20" height="13" rx="2"/>
              <path d="M2 11h20"/>
              <circle cx="17" cy="15" r="1.2" fill="currentColor"/>
            </svg>
            <!-- Users -->
            <svg v-else-if="m.icon === 'users'" width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </span>
          <span>{{ m.label }}</span>
        </button>
      </aside>

      <!-- ===== Module content (right, fills remaining width) ===== -->
      <main class="fv-content">

        <!-- Loading + error states (shared across modules) -->
        <div v-if="isLoading" class="fv-empty">Loading…</div>
        <div v-else-if="permissionDenied" class="fv-perm">
          <div class="fv-perm-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                 stroke="#b45309" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <div class="fv-perm-title">This module needs an upgraded API token</div>
          <div class="fv-perm-body">
            Your Fangate API key for <strong>{{ account.name }}</strong> doesn't
            include the <code>can_view_transactions</code> ability that this
            endpoint requires. To unlock Dashboard + Wallet, regenerate the
            token at <a href="https://app.fangate.info" target="_blank"
            rel="noopener">app.fangate.info → Settings → API</a> with the
            <code>can_view_transactions</code> and
            <code>can_create_new_urls</code> abilities checked, then re-link
            it under <em>Settings → Chat-Accounts → Edit</em>.
          </div>
          <div class="fv-perm-body" style="margin-top:8px;">
            Content Library still works because it only needs the always-on
            <code>get_existing_urls</code> permission.
          </div>
        </div>
        <div v-else-if="fetchError" class="fv-error">{{ fetchError }}</div>

        <!-- ==== CONTENT LIBRARY ==== -->
        <template v-else-if="activeModule === 'content-library'">

          <!-- DETAIL VIEW (when a product was clicked) -->
          <template v-if="detail.product">
            <div class="fv-section-head fv-section-head--detail">
              <button class="fv-back-inline" @click="saveAndCloseDetail"
                      :disabled="detail.saving"
                      :title="detailDirty ? 'Save changes and return to library' : 'Return to library'">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2.2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                {{ detail.saving ? 'Saving…' : (detailDirty ? 'Save & back to library' : 'Back to library') }}
              </button>

              <!-- Top-of-page status row: Moderation + Consent pills shown
                   separately so it's obvious what's missing at a glance.
                   Centered between Back button and Refresh; the Refresh
                   button re-queries /products/{id} + the consent-tag list
                   so the user can pull the new state after Fangate moderation
                   finishes (e.g. "Manual approval" → "Approved"). -->
              <div class="fv-section-head-status">
                <div class="fv-status-cell">
                  <span class="fv-mod-eyebrow">Moderation</span>
                  <span class="fv-mod-pill"
                        :class="'fv-mod-pill--' + (
                          !fullProductLoaded ? 'pending'
                          : moderationCss(moderationSummary ? moderationSummary.overall : 'unknown')
                        )">
                    {{ !fullProductLoaded
                       ? 'Loading…'
                       : moderationLabel(moderationSummary ? moderationSummary.overall : 'unknown') }}
                  </span>
                </div>
                <div class="fv-status-cell">
                  <span class="fv-mod-eyebrow">Consent</span>
                  <template v-if="detail.flags.shouldConsent">
                    <span class="fv-mod-pill"
                          :class="'fv-mod-pill--' + (consentSummary ? consentSummary.overall : 'empty')">
                      {{ consentSummary ? consentLabel(consentSummary.overall) : '—' }}
                    </span>
                  </template>
                  <span v-else class="fv-mod-pill fv-mod-pill--empty">Not required</span>
                </div>
              </div>

              <button class="fv-status-refresh"
                      :disabled="refreshingStatus"
                      :class="{ 'is-spinning': refreshingStatus }"
                      @click="refreshStatus"
                      title="Refresh moderation &amp; consent status">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="23 4 23 10 17 10"/>
                  <polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              </button>
            </div>
            <div class="fv-detail">
              <!-- Large media banner: full-width preview, action buttons row underneath -->
              <div class="fv-detail-media">
                <img v-if="detail.product.preview || detail.product.thumb"
                     :src="detail.product.preview || detail.product.thumb"
                     :alt="detail.product.title"
                     class="fv-detail-media-img" />
                <div v-else class="fv-detail-media-empty">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none"
                       stroke="var(--ink-muted)" stroke-width="1.3" stroke-linecap="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                  <span>No preview available</span>
                </div>
              </div>

              <!-- Per-file moderation breakdown sits under the media banner
                   only when there's something to show (multiple files OR a
                   removal reason on a single file). The Moderation+Consent
                   pills themselves live in the top section-head row. -->
              <div v-if="moderationSummary && (moderationSummary.total > 1 || moderationSummary.items[0]?.removal_description)"
                   class="fv-status">
                <div v-if="moderationSummary.total > 1" class="fv-mod-list">
                  <div v-for="m in moderationSummary.items" :key="m.id" class="fv-mod-item">
                    <div class="fv-mod-item-thumb">
                      <img v-if="m.preview_blurred || m.preview"
                           :src="m.preview_blurred || m.preview" alt="" />
                    </div>
                    <span class="fv-mod-item-type">{{ m.type || 'media' }}</span>
                    <span class="fv-mod-item-status">{{ m.veriff_status || 'Unknown' }}</span>
                    <span v-if="m.removal_description" class="fv-mod-item-reason">
                      {{ m.removal_description }}
                    </span>
                  </div>
                </div>
                <div v-else-if="moderationSummary.items[0]?.removal_description"
                     class="fv-mod-reason">
                  <strong>Reason:</strong> {{ moderationSummary.items[0].removal_description }}
                </div>
              </div>

              <!-- Title + read-only meta + action buttons. The bold title IS
                   the editable surface — click the pencil to switch to an
                   inline input; commit on Enter/blur. -->
              <div class="fv-detail-head">
                <div class="fv-detail-headmeta">
                  <div class="fv-detail-eyebrow">Product #{{ detail.product.id }}</div>
                  <div class="fv-detail-headtitle-row">
                    <span class="fv-detail-headtitle-label">Title:</span>
                    <input v-if="editingTitle"
                           ref="titleInputRef"
                           v-model="detail.title"
                           maxlength="80"
                           placeholder="Product title"
                           class="fv-detail-headtitle-input"
                           @keydown.enter.prevent="commitEditTitle"
                           @keydown.escape.prevent="commitEditTitle"
                           @blur="commitEditTitle" />
                    <span v-else class="fv-detail-headtitle"
                          @dblclick="startEditTitle"
                          :title="detail.title || 'Untitled'">
                      {{ detail.title || 'Untitled' }}
                    </span>
                    <button v-if="!editingTitle"
                            class="fv-detail-headtitle-edit"
                            @click="startEditTitle"
                            title="Edit title">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 20h9"/>
                        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                      </svg>
                      Edit title
                    </button>
                  </div>
                  <div class="fv-detail-headsub">
                    <span v-if="detail.product.type">{{ detail.product.type }}</span>
                    <span v-if="detail.product.items">· {{ detail.product.items }} items</span>
                    <span v-if="detail.product.total_earnings != null">
                      · {{ formatBalance(detail.product.total_earnings) }} earned
                    </span>
                  </div>
                </div>
                <div class="fv-detail-actions">
                  <button class="fv-detail-action"
                          :class="{ 'is-copied': copiedFlash }"
                          @click="copyProductLink"
                          :title="productLink">
                    <svg v-if="!copiedFlash" width="13" height="13" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2.4" stroke-linecap="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {{ copiedFlash ? 'Copied!' : 'Copy link' }}
                  </button>
                  <button class="fv-detail-action fv-detail-action--primary"
                          @click="previewProductLink"
                          :disabled="!productLink"
                          :title="productLink || 'Open checkout page in new tab'">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                    Preview link
                  </button>
                  <button class="fv-detail-action fv-detail-action--primary"
                          @click="previewProductContent"
                          :disabled="!productContentUrl"
                          :title="productContentUrl || 'Open content file in new tab'">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <circle cx="10" cy="14" r="2"/>
                      <path d="M20 17l-3-3-5 5"/>
                    </svg>
                    Preview content
                  </button>
                </div>
              </div>

              <!-- Variant picker — only when this product is a clone cluster.
                   Switching here scopes the form to that specific Fangate
                   product, so Save mutates only that variant's price/metadata. -->
              <div v-if="detail.cluster && detail.cluster.length > 1" class="fv-detail-variants">
                <label class="fv-detail-label">Price link ({{ detail.cluster.length }} variants)</label>
                <select :value="detail.product.id"
                        @change="switchVariant($event.target.value)"
                        class="fv-detail-input fv-detail-variants-select">
                  <option v-for="(v, i) in detail.cluster" :key="v.id" :value="v.id">
                    Link {{ i + 1 }} · {{ formatMoney(v.price) }}{{ v.id === detail.product.id ? '  (current)' : '' }}
                  </option>
                </select>
              </div>

              <!-- Editable form. Title now lives inline in the head card
                   above (click the Edit pencil) — no separate row here. -->
              <div class="fv-detail-form">
                <div class="fv-detail-row-2col">
                  <div>
                    <label class="fv-detail-label">Price</label>
                    <div class="fv-detail-price-wrap">
                      <span class="fv-detail-price-prefix">€</span>
                      <!-- type="text" + inputmode="decimal" avoids the browser's
                           number-input spinners entirely (scoped CSS can't
                           always reach them via ::-webkit-*). Range is
                           re-validated in saveProductDetail. -->
                      <input v-model="detail.price" type="text" inputmode="decimal"
                             placeholder="0.00" class="fv-detail-price-input" />
                      <!-- Inline "Edit price" trigger — sits inside the price
                           field on the right. Opens the FangatePriceModal so
                           the user can change the price OR create a new link. -->
                      <button class="fv-detail-price-edit"
                              @click.stop="openPriceModalForDetail"
                              title="Open the price editor">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M12 20h9"/>
                          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                        </svg>
                        Edit price
                      </button>
                    </div>
                    <div class="fv-detail-hint">Min: 5 &nbsp; Max: 224</div>
                  </div>
                  <div>
                    <label class="fv-detail-label">Folder</label>

                    <!-- PICK mode: dropdown selector listing existing folders + "+ New folder" -->
                    <div v-if="folderMode === 'pick'" class="fv-folder-pick">
                      <button class="fv-folder-btn"
                              :class="{ open: folderPickerOpen }"
                              @click.stop="folderPickerOpen = !folderPickerOpen">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                        </svg>
                        <span :class="{ 'is-empty': !detail.folder }">
                          {{ detail.folder || 'No folder' }}
                        </span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="2.4" stroke-linecap="round"
                             style="margin-left:auto;flex-shrink:0;">
                          <path d="m6 9 6 6 6-6"/>
                        </svg>
                      </button>
                      <div v-if="folderPickerOpen" class="fv-folder-menu" @click.stop>
                        <button class="fv-folder-row" @click="clearFolder">
                          <span class="fv-folder-row-name is-muted">No folder</span>
                          <svg v-if="detail.folderId == null" width="11" height="11" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </button>
                        <div v-if="folderOptions.length" class="fv-folder-sep"></div>
                        <button v-for="f in folderOptions" :key="f.id" class="fv-folder-row"
                                @click="setFolder(f)">
                          <span class="fv-folder-row-name">{{ f.name }}</span>
                          <svg v-if="Number(detail.folderId) === Number(f.id)"
                               width="11" height="11" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </button>
                        <div class="fv-folder-sep"></div>
                        <button class="fv-folder-row fv-folder-row--new" @click="startNewFolder">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                            <path d="M12 5v14M5 12h14"/>
                          </svg>
                          New folder…
                        </button>
                      </div>
                    </div>

                    <!-- NEW mode: type a name, Enter (or ✓) creates it on Fangate -->
                    <div v-else class="fv-folder-new">
                      <input v-model="newFolderName" maxlength="50"
                             placeholder="New folder name"
                             class="fv-detail-input"
                             @keydown.enter.prevent="confirmNewFolder"
                             autofocus />
                      <button class="fv-folder-cancel"
                              :disabled="newFolderSaving || !newFolderName.trim()"
                              @click="confirmNewFolder"
                              title="Create folder">
                        {{ newFolderSaving ? '…' : '✓' }}
                      </button>
                      <button class="fv-folder-cancel" @click="cancelNewFolder" title="Cancel">×</button>
                    </div>
                  </div>
                </div>

                <div class="fv-detail-row">
                  <label class="fv-detail-label">Public description</label>
                  <textarea v-model="detail.publicDesc" maxlength="500" rows="3"
                            placeholder="Visible to buyers"
                            class="fv-detail-textarea"/>
                </div>

                <div class="fv-detail-row">
                  <label class="fv-detail-label">Internal notes</label>
                  <textarea v-model="detail.internalNotes" maxlength="500" rows="2"
                            placeholder="Private — only you can see this"
                            class="fv-detail-textarea"/>
                </div>

                <div class="fv-detail-row">
                  <label class="fv-detail-label">Access &amp; compliance</label>
                  <div class="fv-detail-flags">
                    <div class="fv-detail-flag">
                      <div>
                        <div class="fv-detail-flag-title">AVS (18+)</div>
                        <div class="fv-detail-flag-desc">Mark as adult content.</div>
                      </div>
                      <button class="fv-switch" :class="{ on: detail.flags.avs18 }"
                              @click="toggleDetailFlag('avs18')">
                        <span class="fv-switch-knob"></span>
                      </button>
                    </div>
                    <div class="fv-detail-flag" :class="{ 'is-disabled': !detail.flags.avs18 }">
                      <div>
                        <div class="fv-detail-flag-title">Require age check</div>
                        <div class="fv-detail-flag-desc">Only available when AVS is on.</div>
                      </div>
                      <button class="fv-switch"
                              :class="{ on: detail.flags.agecheck, disabled: !detail.flags.avs18 }"
                              :disabled="!detail.flags.avs18"
                              @click="toggleDetailFlag('agecheck')">
                        <span class="fv-switch-knob"></span>
                      </button>
                    </div>
                    <div class="fv-detail-flag">
                      <div>
                        <div class="fv-detail-flag-title">Allow download</div>
                        <div class="fv-detail-flag-desc">Buyers can download after purchase.</div>
                      </div>
                      <button class="fv-switch" :class="{ on: detail.flags.download }"
                              @click="toggleDetailFlag('download')">
                        <span class="fv-switch-knob"></span>
                      </button>
                    </div>
                    <div class="fv-detail-flag">
                      <div>
                        <div class="fv-detail-flag-title">Requires third-party consent</div>
                        <div class="fv-detail-flag-desc">
                          If a third person is visible in this content, consent
                          must be obtained by tagging them — provide their name
                          and email below. Fangate will email this address with
                          the consent request.
                        </div>
                      </div>
                      <button class="fv-switch" :class="{ on: detail.flags.shouldConsent }"
                              @click="toggleDetailFlag('shouldConsent')">
                        <span class="fv-switch-knob"></span>
                      </button>
                    </div>
                    <div class="fv-detail-flag">
                      <div>
                        <div class="fv-detail-flag-title">Add to collection</div>
                        <div class="fv-detail-flag-desc">Include on your public collection page.</div>
                      </div>
                      <button class="fv-switch" :class="{ on: detail.flags.collection }"
                              @click="toggleDetailFlag('collection')">
                        <span class="fv-switch-knob"></span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Co-creator tagging (API-backed). Only shown when the
                     "Requires third-party consent" toggle above is on. -->
                <div v-if="detail.flags.shouldConsent" class="fv-detail-row">
                  <label class="fv-detail-label">Co-creators &amp; third-party consent</label>
                  <div class="fv-co">
                    <!-- Existing tags chip list -->
                    <div v-if="coTags.loading" class="fv-co-loading">Loading tags…</div>
                    <div v-else-if="coTags.items.length" class="fv-co-chips">
                      <div v-for="t in coTags.items" :key="t.id"
                           class="fv-co-chip"
                           :class="'fv-co-chip--' + (t.status || 'pending')">
                        <span class="fv-co-chip-avatar">
                          {{ getInitials(creatorLabel(t.tagged_user || t)) }}
                        </span>
                        <span class="fv-co-chip-name">
                          {{ creatorLabel(t.tagged_user || t) }}
                        </span>
                        <span class="fv-co-chip-status">{{ t.status || 'pending' }}</span>
                      </div>
                    </div>

                    <!-- Always-visible search. Type name/email → results list
                         appears below — each result has its own "Tag creator"
                         button that fires the consent request immediately. -->
                    <input v-model="coTags.addText" @input="onSearchInput"
                           placeholder="Search creators by name or email…"
                           class="fv-detail-input fv-co-searchinput" />

                    <div v-if="coTags.searching" class="fv-co-hint">Searching…</div>
                    <div v-else-if="coTags.searchError" class="fv-co-err">
                      {{ coTags.searchError }}
                    </div>
                    <div v-else-if="coTags.results.length" class="fv-co-results">
                      <div v-for="c in coTags.results" :key="c.id" class="fv-co-result">
                        <span class="fv-co-result-avatar">{{ getInitials(creatorLabel(c)) }}</span>
                        <div class="fv-co-result-meta">
                          <div class="fv-co-result-name">{{ creatorLabel(c) }}</div>
                          <div v-if="c.email" class="fv-co-result-email">{{ c.email }}</div>
                        </div>
                        <button class="fv-co-tagbtn"
                                :disabled="taggingId === c.id"
                                @click="tagCreatorFromResult(c)">
                          {{ taggingId === c.id ? 'Tagging…' : 'Tag creator' }}
                        </button>
                      </div>
                    </div>
                    <div v-else-if="coTags.addText.trim().length >= 2"
                         class="fv-co-noresults">
                      <div class="fv-co-hint">
                        No matches on Fangate for "{{ coTags.addText.trim() }}".
                      </div>
                      <!-- Email-fallback: when the typed string is a valid
                           email, Fangate's POST /consents/store-or-send
                           accepts an `email` mode that sends an invite if
                           the recipient isn't yet a Fangate user (or
                           auto-converts to creator-tag if they are). -->
                      <button v-if="showEmailInvite"
                              class="fv-co-invitebtn"
                              :disabled="invitingEmail"
                              @click="inviteByEmail">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                        {{ invitingEmail
                            ? 'Sending…'
                            : `Send consent request to ${coTags.addText.trim()}` }}
                      </button>
                    </div>
                    <div v-else-if="!coTags.items.length" class="fv-co-empty">
                      Start typing to find a co-creator on Fangate.
                    </div>

                    <div v-if="coTags.addError" class="fv-co-err">{{ coTags.addError }}</div>
                    <div v-if="coTags.error" class="fv-co-err">{{ coTags.error }}</div>
                  </div>
                </div>

                <div v-if="detail.error" class="fv-detail-err">{{ detail.error }}</div>

                <div class="fv-detail-foot">
                  <button class="fv-detail-cancel" @click="closeDetail">Cancel</button>
                  <button class="fv-detail-save"
                          :disabled="detail.saving"
                          @click="saveProductDetail">
                    {{ detail.saving ? 'Saving…' : 'Save changes' }}
                  </button>
                </div>
              </div>
            </div>
          </template>

          <!-- GRID VIEW (default) -->
          <template v-else>
            <div class="fv-section-head">
              <div class="fv-section-h">Content Library</div>
            </div>

            <!-- Toolbar: folder picker + search + type filter + sort + refresh -->
            <div class="fv-lib-toolbar">
              <FangateContentToolbar
                v-model:search="search"
                v-model:folder="selectedFolder"
                v-model:sort="sortMode"
                :active-types="[...activeFilters.type]"
                :folders="folders"
                :filter-count="filterCount"
                :is-loading-products="isLoading"
                @toggle-type="toggleFilter('type', $event)"
                @clear-filters="clearFilters"
                @refresh="loadProducts({ force: true })"
              />
            </div>

            <!-- Moderation status filter + top pagination row -->
            <div class="fv-lib-filter-row" v-if="products.length">
              <div class="fv-mod-filter">
                <button v-for="opt in [
                          { id: 'all',       label: 'All' },
                          { id: 'approved',  label: 'Approved' },
                          { id: 'in_review', label: 'In review' },
                          { id: 'rejected',  label: 'Rejected' },
                        ]"
                        :key="opt.id"
                        class="fv-mod-filter-chip"
                        :class="{ active: moderationFilter === opt.id }"
                        @click="moderationFilter = opt.id">
                  {{ opt.label }}
                </button>
              </div>
              <div class="fv-lib-filter-right">
                <!-- Reload — corporate-teal labeled button. Pulls a fresh
                     products list from Fangate (will hit the cache layer
                     once the per-account TTL cache is wired). -->
                <button class="fv-reload-btn"
                        :disabled="isLoading"
                        :title="isLoading ? 'Reloading…' : 'Reload from Fangate (bypass cache)'"
                        @click="loadProducts({ force: true })">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2.2"
                       stroke-linecap="round" stroke-linejoin="round"
                       :class="{ 'fv-spin': isLoading }">
                    <polyline points="23 4 23 10 17 10"/>
                    <polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  {{ isLoading ? 'Reloading…' : 'Reload' }}
                </button>
                <div v-if="totalPages > 1" class="fv-pager">
                  <button class="fv-pager-btn" :disabled="currentPage <= 1" @click="prevPage" title="Previous page">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="15 18 9 12 15 6"/>
                    </svg>
                  </button>
                  <span class="fv-pager-label">Page {{ currentPage }} / {{ totalPages }}</span>
                  <button class="fv-pager-btn" :disabled="currentPage >= totalPages" @click="nextPage" title="Next page">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="!products.length" class="fv-empty">No products yet.</div>
            <div v-else-if="!sortedProducts.length" class="fv-empty">
              No products match the current filters.
            </div>
            <div v-else class="fv-grid">
              <div v-for="p in pagedProducts" :key="p.id" class="fv-prod"
                   role="button" tabindex="0"
                   @click="openProductDetail(p)"
                   @keydown.enter.prevent="openProductDetail(p)"
                   :title="`Click to open ${p.title || 'product'}`">
                <div class="fv-prod-thumb">
                  <img v-if="p.preview || p.thumb" :src="p.preview || p.thumb" :alt="p.title" />
                  <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none"
                       stroke="var(--ink-muted)" stroke-width="1.8" stroke-linecap="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                </div>
                <div class="fv-prod-meta">
                  <div class="fv-prod-title">{{ p.title || `Product #${p.id}` }}</div>
                  <div class="fv-prod-sub">
                    <span v-if="variantCountOf(p.id)" class="fv-prod-linkcount">
                      {{ variantCountOf(p.id) }} links
                    </span>
                    <span v-if="variantCountOf(p.id)">·&nbsp;</span>{{ p.type || 'Product' }}<span v-if="p.items">&nbsp;·&nbsp;{{ p.items }} items</span>
                    <span class="fv-prod-pid">#{{ p.id }}</span>
                  </div>
                  <div class="fv-prod-price">
                    <template v-if="priceRangeOf(p.id)">
                      {{ formatMoney(priceRangeOf(p.id).min) }} – {{ formatMoney(priceRangeOf(p.id).max) }}
                    </template>
                    <template v-else>
                      {{ formatMoney(p.price) }}
                    </template>
                  </div>
                </div>
                <!-- Right-side status column: traffic-light dot per row.
                     Compact (no truncation), full label on hover. The dot
                     reads its colour straight from moderationCss / consentGridCss
                     so the existing 3-bucket palette (red/orange/green/gray)
                     stays the single source of truth. -->
                <div class="fv-prod-status">
                  <div class="fv-prod-status-row"
                       :title="'Moderation: ' + moderationLabel(productModerationKey(p) || 'unknown')">
                    <span class="fv-prod-status-label">Moderation</span>
                    <span class="fv-prod-status-dot"
                          :class="'fv-prod-status-dot--' + (productModerationKey(p) ? moderationCss(productModerationKey(p)) : 'unknown')"></span>
                  </div>
                  <div class="fv-prod-status-row"
                       :title="'Consent: ' + consentGridLabel(productConsentKey(p))">
                    <span class="fv-prod-status-label">Consent</span>
                    <span class="fv-prod-status-dot"
                          :class="'fv-prod-status-dot--' + consentGridCss(productConsentKey(p))"></span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bottom pager — same controls as the top row so the user
                 doesn't have to scroll back up after browsing 30 items. -->
            <div v-if="totalPages > 1 && sortedProducts.length" class="fv-pager-bottom">
              <button class="fv-pager-btn" :disabled="currentPage <= 1" @click="prevPage" title="Previous page">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <span class="fv-pager-label">Page {{ currentPage }} / {{ totalPages }}</span>
              <button class="fv-pager-btn" :disabled="currentPage >= totalPages" @click="nextPage" title="Next page">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
          </template>
        </template>

        <!-- ==== WALLET ==== -->
        <template v-else-if="activeModule === 'wallet'">
          <div class="fv-section-h">Wallet</div>
          <div v-if="!wallet" class="fv-empty">No wallet data.</div>
          <template v-else>
            <div class="fv-kpi-grid">
              <div class="fv-kpi-card fv-kpi-card--accent">
                <div class="fv-kpi-label">Total</div>
                <div class="fv-kpi-value">{{ formatBalance(centsToEuro(wallet.total)) }}</div>
              </div>
              <div class="fv-kpi-card fv-kpi-card--avail">
                <div class="fv-kpi-label">Available for cashout</div>
                <div class="fv-kpi-value">{{ formatBalance(centsToEuro(wallet.available)) }}</div>
              </div>
              <div class="fv-kpi-card">
                <div class="fv-kpi-label">Pending</div>
                <div class="fv-kpi-value">{{ formatBalance(centsToEuro(wallet.pending)) }}</div>
              </div>
              <div class="fv-kpi-card fv-kpi-card--hold">
                <div class="fv-kpi-label">Hold</div>
                <div class="fv-kpi-value">{{ formatBalance(centsToEuro(wallet.hold)) }}</div>
              </div>
            </div>

            <div class="fv-wallet-cols">
              <!-- Left: wallet ledger -->
              <div class="fv-wallet-col">
                <div class="fv-section-sh">Recent transactions</div>
                <div v-if="!wallet.transactions?.length" class="fv-empty">No transactions yet.</div>
                <div v-else class="fv-list">
                  <div v-for="tx in wallet.transactions" :key="tx.id" class="fv-list-row">
                    <div class="fv-list-meta">
                      <div class="fv-list-title">{{ tx.title || tx.type || 'Sale' }}</div>
                      <div class="fv-list-sub">{{ formatRelTime(tx.created_at) }}</div>
                    </div>
                    <div class="fv-list-amount">{{ formatMoney(centsToEuro(tx.amount)) }}</div>
                  </div>
                </div>
              </div>

              <!-- Right: top performing products (by aggregate revenue) -->
              <div class="fv-wallet-col">
                <div class="fv-section-sh">Top performing products</div>
                <div v-if="!topProducts.length" class="fv-empty">No products yet.</div>
                <div v-else class="fv-list">
                  <div v-for="p in topProducts" :key="'tp-' + p.id" class="fv-top-row">
                    <div class="fv-top-thumb">
                      <img v-if="p.preview || p.preview_blurred" :src="p.preview || p.preview_blurred"
                           :alt="p.title || 'product'" loading="lazy" />
                      <span v-else>{{ (p.type || 'item').slice(0, 3) }}</span>
                    </div>
                    <div class="fv-top-meta">
                      <div class="fv-list-title" :title="p.title">{{ p.title || 'Untitled' }}</div>
                      <div class="fv-list-sub">{{ formatMoney(p.price) }} · {{ p.unlocks || 0 }} sold</div>
                    </div>
                    <div class="fv-list-amount">{{ formatMoney(p.total_earnings || 0) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>

        <!-- ==== UPLOAD CONTENT ====
             No standalone module body — clicking the Upload tile in the
             module sidebar opens FangateUploadModal as overlay (see
             selectModule + <FangateUploadModal> at the root of fv-root).
             activeModule is never set to 'upload', so this branch would be
             unreachable; the placeholder template was removed entirely. -->

        <!-- ==== COLLECTION ==== -->
        <!-- Lists products currently on the creator's public collection page
             (GET /api/products/collection). Each card has a "Remove" action
             that calls the toggle endpoint and drops the product optimistically.
             Adding to collection happens elsewhere — either via the Vault
             detail flag or the side-panel upload modal. -->
        <template v-else-if="activeModule === 'collection'">
          <div class="fv-section-head">
            <div class="fv-section-h">Collection</div>
            <button class="fv-tool-btn" :disabled="isLoadingCollection"
                    @click="loadCollection({ force: true })" title="Refresh (bypass cache)">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>

          <div v-if="isLoadingCollection" class="fv-empty">Loading…</div>
          <div v-else-if="!collectionProducts.length" class="fv-empty">
            No products in your collection yet.<br/>
            <span style="font-size:12px;color:var(--ink-muted);">
              Toggle a product's "Add to Collection" flag in the Library detail view
              or in the upload modal to include it here.
            </span>
          </div>
          <div v-else class="fv-grid">
            <div v-for="p in collectionProducts" :key="p.id" class="fv-prod">
              <div class="fv-prod-thumb">
                <img v-if="p.preview || p.thumb" :src="p.preview || p.thumb" :alt="p.title" />
                <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none"
                     stroke="var(--ink-muted)" stroke-width="1.8" stroke-linecap="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
              </div>
              <div class="fv-prod-meta">
                <div class="fv-prod-title">{{ p.title || `Product #${p.id}` }}</div>
                <div class="fv-prod-sub">
                  {{ p.type || 'Product' }}<span v-if="p.items">&nbsp;·&nbsp;{{ p.items }} items</span>
                  <span class="fv-prod-pid">#{{ p.id }}</span>
                </div>
                <div class="fv-prod-price">{{ formatMoney(p.price) }}</div>
              </div>
              <button class="fv-collection-remove"
                      @click.stop="removeFromCollection(p)"
                      title="Remove from collection">
                Remove
              </button>
            </div>
          </div>
        </template>

        <!-- ==== COLLABORATIONS — co-creator consent / tag management ==== -->
        <template v-else-if="activeModule === 'collaborations'">
          <FangateCollaborations :account-name="account?.name || ''" />
        </template>

        <!-- ==== EMPTY (no module picked yet) — landing screen ==== -->
        <template v-else>
          <div class="fv-placeholder fv-placeholder--lead">
            <div class="fv-placeholder-icon fv-placeholder-icon--lead">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="1.8"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="1"/>
              </svg>
            </div>
            <div class="fv-placeholder-title">Pick a module to begin</div>
            <div class="fv-placeholder-body">
              Choose <strong>Content Library</strong>, <strong>Collection</strong>
              or <strong>Wallet</strong> on the left to load this account's data.
            </div>
          </div>
        </template>

      </main>
    </div>

    <!-- Upload modal — opened from the "Upload Content" tile in the module
         sidebar. Overlays the Vault; success triggers a products refresh so
         the new product appears in the Library grid behind it on close. -->
    <FangateUploadModal
      v-model:open="uploadModalOpen"
      :vault-name="account?.name || 'Creator'"
      :is-connected="true"
      :format-balance="formatBalance"
      currency-symbol="€"
      @uploaded="onVaultUploaded"
    />

    <!-- Edit-Price modal — reused from side panel. Opened from the
         "Change price · + New link" button next to the price field in the
         detail view. Modal handles its own API calls (PATCH /price for
         change, POST /price-links for new) and emits the saved state. -->
    <FangatePriceModal
      v-model:open="priceModalOpen"
      :product="detail.product"
      :active-variant="detail.product"
      :is-connected="true"
      :format-balance="formatBalance"
      currency-symbol="€"
      @saved-change-price="onPriceModalChanged"
      @saved-new-link="onPriceModalNewLink"
    />
  </div>
</template>

<style lang="scss" scoped>
.fv-root {
  display: flex; flex-direction: column;
  width: 100%; height: 100%;
  background: var(--bg-app);
  overflow: hidden;
}

// ---- Mobile (≤768px): cream background (matches Labels) + single-pane --------
// The desktop modnav-sidebar + content sat side-by-side, so the content pane's
// API error showed next to the module buttons. On phones it's a drill-down:
// the landing shows the module list only; opening a module swaps to its content
// (modnav hidden, the top Back button returns to the landing).
@media (max-width: 768px) {
  .fv-root { background: var(--bg-middle); }
  .fv-body { padding: 12px; gap: 0; }
  .fv-root:not(.fv-mod-open) .fv-modnav { width: 100%; }
  .fv-root:not(.fv-mod-open) .fv-content { display: none; }
  .fv-root.fv-mod-open .fv-modnav { display: none; }
  .fv-root.fv-mod-open .fv-content { flex: 1; }
}

// ===== Top bar =====
.fv-top {
  display: flex; align-items: center; gap: 14px;
  padding: 10px 18px;
  border-bottom: 1px solid var(--border-soft);
  background: var(--bg-panel);
  flex-shrink: 0;
}
.fv-back {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 10px; border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle); color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  &:hover { border-color: rgba(31, 42, 42, 0.4); color: var(--ink-strong); }
}
.fv-breadcrumb {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
}
.fv-bc-muted { color: var(--ink-muted); margin-right: 4px; }
.fv-bc-name  { color: var(--ink-strong); font-weight: 700; }

// ===== Vault body =====
.fv-body {
  flex: 1; min-height: 0;
  display: flex; gap: 18px;
  padding: 18px;
  overflow: hidden;
}

// ===== Module sidebar =====
.fv-modnav {
  width: 230px; flex-shrink: 0;
  display: flex; flex-direction: column; gap: 6px;
  overflow-y: auto;
}
.fv-brand {
  display: flex; flex-direction: column; align-items: center;
  padding: 6px 0 14px;
  text-align: center;
}
.fv-brand-svg {
  width: 150px; height: auto;
  display: block;
}
.fv-welcome {
  margin-top: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 700;
  text-align: center;
  background: linear-gradient(90deg, #1d2dad 0%, #4d2db0 35%, #8a25b8 65%, #c01ab0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.fv-modbtn {
  display: flex; align-items: center; gap: 11px;
  padding: 11px 14px;
  border-radius: 12px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-strong);
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: all 0.12s;
  &:hover {
    background: var(--bg-subtle);
    border-color: rgba(74, 53, 32, 0.2);
  }
  &--active {
    background: var(--accent-tint);
    color: var(--accent-strong);
    border-color: var(--accent-ring);
  }
  &--primary {
    padding: 14px;
    background: var(--accent-soft);
    color: var(--accent-strong);
    border: 1.5px solid var(--accent-ring);
    font-size: 14px; font-weight: 700;
  }
  &--primary.fv-modbtn--active {
    background: var(--accent-tint);
  }
  &--gap { margin-bottom: 12px; }
}
.fv-modbtn-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border-radius: 8px;
  background: var(--bg-subtle);
  color: var(--ink-medium);
  flex-shrink: 0;
  .fv-modbtn--active & {
    background: var(--accent-soft);
    color: var(--accent-strong);
  }
  .fv-modbtn--primary & {
    width: 34px; height: 34px;
  }
}

// ===== Module content (right pane) =====
.fv-content {
  flex: 1; min-width: 0;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  padding: 24px;
  overflow-y: auto;
}
.fv-section-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
  gap: 12px;
}
// Detail-view variant: hosts the Back button on the left and the
// Moderation+Consent status row centered between Back and the Refresh
// icon button on the right.
.fv-section-head--detail {
  align-items: center;
  gap: 12px;
  // Grid lets us pin Back left + Refresh right while the status pill
  // naturally lands in the middle (center column auto-sized to content).
  display: grid;
  grid-template-columns: 1fr auto 1fr;
}
.fv-section-head--detail > .fv-back-inline { justify-self: start; }
.fv-section-head--detail > .fv-status-refresh { justify-self: end; }
.fv-section-head-status {
  display: flex; align-items: center; gap: 20px;
  padding: 6px 14px;
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 999px;
  // Centered in its grid column.
  justify-self: center;
}
// Refresh icon button — matches the rest of the chrome (subtle gray, hover
// → corporate green). Spins while a refresh is in flight.
.fv-status-refresh {
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  cursor: pointer;
  transition: all 0.12s;
  flex-shrink: 0;
  &:hover:not(:disabled) {
    border-color: var(--accent-ring);
    color: #047857;
    background: rgba(16, 185, 129, 0.08);
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &.is-spinning svg { animation: fv-spin 0.9s linear infinite; }
}
@keyframes fv-spin {
  to { transform: rotate(360deg); }
}
.fv-section-h {
  font-family: 'Inter', sans-serif;
  font-size: 18px; font-weight: 800;
  color: var(--ink-strong);
  margin-bottom: 14px;
}
.fv-section-sh {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--ink-muted);
  margin: 18px 0 8px;
}
.fv-tool-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  padding: 0; border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle); color: var(--ink-medium);
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: rgba(31, 42, 42, 0.4);
    color: var(--ink-strong);
  }
  &:disabled { opacity: 0.6; cursor: wait; }
}

// Library toolbar wrapper — strips FangateContentToolbar's bottom border
// (it was sized for the narrow side panel where the border separates the
// toolbar from the product list; in the Library the spacing is enough).
.fv-lib-toolbar {
  margin-bottom: 12px;
  :deep(.fp-toolbar) {
    padding: 0;
    border-bottom: none;
    background: transparent;
  }
}

.fv-empty, .fv-error {
  font-family: 'Inter', sans-serif;
  font-size: 13px; color: var(--ink-muted);
  text-align: center; padding: 36px 16px;
}
.fv-error { color: #b91c1c; }

// Permission-denied card — distinct from generic errors. Shows what the
// token is missing and how to fix it.
.fv-perm {
  background: linear-gradient(180deg, rgba(245, 158, 11, 0.08), transparent);
  border: 1.5px dashed rgba(245, 158, 11, 0.4);
  border-radius: 14px;
  padding: 28px 24px;
  text-align: center;
  max-width: 640px;
  margin: 12px auto;
}
.fv-perm-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 52px; height: 52px;
  border-radius: 50%;
  background: rgba(245, 158, 11, 0.14);
  margin-bottom: 10px;
}
.fv-perm-title {
  font-family: 'Inter', sans-serif;
  font-size: 15px; font-weight: 800;
  color: #92400e;
  margin-bottom: 8px;
}
.fv-perm-body {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; line-height: 1.55;
  color: #78350f;
  code {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11.5px;
    background: rgba(255, 255, 255, 0.7);
    padding: 1px 5px;
    border-radius: 4px;
    color: #7c2d12;
  }
  a {
    color: #b45309;
    text-decoration: underline;
    font-weight: 700;
  }
  strong { color: #7c2d12; }
  em { color: #92400e; font-style: italic; }
}

// ===== KPI grid =====
.fv-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 8px;
}
.fv-kpi-card {
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  padding: 14px 16px;
  &--accent {
    background: linear-gradient(135deg, rgba(91, 46, 207, 0.06), rgba(112, 64, 224, 0.04));
    border-color: rgba(91, 46, 207, 0.18);
  }
  // Green tint: "Available for cashout" — positive, payable balance.
  &--avail {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(5, 150, 105, 0.04));
    border-color: rgba(16, 185, 129, 0.25);
    .fv-kpi-label { color: #047857; }
    .fv-kpi-value { color: #047857; }
  }
  // Orange tint: "Hold" — temporarily withheld funds.
  &--hold {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(217, 119, 6, 0.04));
    border-color: rgba(245, 158, 11, 0.28);
    .fv-kpi-label { color: #b45309; }
    .fv-kpi-value { color: #b45309; }
  }
}
.fv-kpi-label {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--ink-muted);
  margin-bottom: 6px;
}
.fv-kpi-value {
  font-family: 'Inter', sans-serif;
  font-size: 22px; font-weight: 800;
  color: var(--ink-strong);
}

// ===== Lists (top products, transactions) =====
// Wallet: transactions (left) + top products (right), side by side; stacks on
// narrow panels.
.fv-wallet-cols {
  display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
  align-items: start;
}
@media (max-width: 900px) { .fv-wallet-cols { grid-template-columns: 1fr; } }
.fv-wallet-col { min-width: 0; }
.fv-top-row {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px;
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
}
.fv-top-thumb {
  width: 38px; height: 38px; flex-shrink: 0;
  border-radius: 8px; overflow: hidden;
  background: var(--bg-panel); border: 1px solid var(--border-soft);
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; color: var(--ink-muted); text-transform: uppercase;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fv-top-meta { min-width: 0; flex: 1; }

.fv-list { display: flex; flex-direction: column; gap: 6px; }
.fv-list-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 14px;
  padding: 10px 14px;
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
}
.fv-list-meta { min-width: 0; flex: 1; }
.fv-list-title {
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 600;
  color: var(--ink-strong);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fv-list-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: var(--ink-muted);
  margin-top: 2px;
}
.fv-list-amount {
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 800;
  color: rgba(31, 42, 42, 0.85);
  flex-shrink: 0;
}

// ===== Product grid (content library) =====
// Filter chips + top pager share a row above the grid.
.fv-lib-filter-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin: 8px 0 12px;
}
.fv-mod-filter {
  display: flex; gap: 6px; flex-wrap: wrap;
}
.fv-mod-filter-chip {
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 700;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  cursor: pointer;
  transition: all 0.12s;
  &:hover { border-color: var(--accent-ring); color: var(--accent-strong); }
  &.active {
    background: #047857;
    border-color: #047857;
    color: #fff;
  }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid #047857; outline-offset: 2px; }
}

// Right cluster of the filter row — Reload button + pager (when present)
// sit at the far right of the moderation-filter strip.
.fv-lib-filter-right {
  display: flex; align-items: center; gap: 10px;
  flex-shrink: 0;
}
// Labeled Reload button — corporate teal-tinted background so it reads as
// a "go fetch fresh" affordance distinct from neutral toolbar icons.
.fv-reload-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid var(--accent-ring);
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 700;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  &:hover:not(:disabled) {
    background: var(--accent-strong);
    color: #fff;
    border-color: var(--accent-strong);
  }
  &:disabled { opacity: 0.65; cursor: progress; }
  svg { flex-shrink: 0; }
}
@keyframes fv-rot { to { transform: rotate(360deg); } }
.fv-spin { animation: fv-rot 0.9s linear infinite; transform-origin: 50% 50%; }

// Pager — same controls top and bottom right.
.fv-pager {
  display: inline-flex; align-items: center; gap: 8px;
}
.fv-pager-bottom {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
.fv-pager-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  cursor: pointer;
  transition: all 0.12s;
  &:hover:not(:disabled) {
    border-color: var(--accent-ring);
    color: #047857;
    background: rgba(16, 185, 129, 0.08);
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid #047857; outline-offset: 2px; }
}
.fv-pager-label {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  color: var(--ink-medium);
  padding: 0 4px;
}

.fv-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}
.fv-prod {
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  padding: 12px;
  display: flex; gap: 10px;
  align-items: center;
  cursor: pointer;
  outline: none;
  transition: box-shadow 0.12s, border-color 0.12s, transform 0.12s;
  &:hover {
    box-shadow: 0 4px 16px rgba(11, 20, 20, 0.08);
    border-color: rgba(91, 46, 207, 0.45);
    transform: translateY(-1px);
  }
  &:focus-visible {
    border-color: var(--accent-strong);
    box-shadow: 0 0 0 3px rgba(91, 46, 207, 0.2);
  }
  &:active { transform: translateY(0); }
}
.fv-prod-thumb {
  width: 56px; height: 56px;
  border-radius: 9px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fv-prod-meta { min-width: 0; flex: 1; }
// Small purple pill in the sub-line — "N" link count when the card
// represents multiple Fangate products clustered as one. Inline + readable
// (the earlier corner-badge variant was clipped by the thumb's overflow:hidden).
.fv-prod-linkcount {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 16px; height: 16px;
  padding: 0 7px;
  margin-right: 4px;
  border-radius: 999px;
  // Corporate Creator-Chat teal — matches sidebar active state + Vault
  // landing accents. Replaces the earlier purple-Fangate gradient.
  background: linear-gradient(135deg, var(--accent-strong), var(--accent));
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 800;
  line-height: 1;
  // Keep "6 links" on a single line — the card sub-row is narrow so the
  // text was wrapping inside the pill and breaking the visual shape.
  white-space: nowrap;
  flex-shrink: 0;
}
.fv-prod-title {
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 700;
  color: var(--ink-strong);
  // Title must always stay fully visible — wrap (incl. long unbreakable strings)
  // instead of truncating with an ellipsis.
  white-space: normal; overflow-wrap: anywhere; line-height: 1.25;
}
.fv-prod-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: var(--ink-muted);
  margin-top: 2px;
}
.fv-prod-pid {
  display: inline-block; margin-left: 5px;
  background: var(--bg-subtle); border-radius: 5px; padding: 0 5px;
  font-size: 10px; color: var(--ink-muted);
  font-variant-numeric: tabular-nums;
}
.fv-prod-price {
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 800;
  color: rgba(31, 42, 42, 0.85);
  margin-top: 4px;
}
// Right-side stacked status column inside each card. Two rows, each a
// label + a colored dot that encodes the state. Hover for the full label.
.fv-prod-status {
  display: flex; flex-direction: column; gap: 6px;
  align-items: flex-end;
  flex-shrink: 0;
  min-width: 0;
}
.fv-prod-status-row {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  color: var(--ink-muted);
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: help;
}
.fv-prod-status-label {
  text-transform: uppercase;
  font-size: 9.5px;
  letter-spacing: 0.06em;
}
.fv-prod-status-dot {
  width: 10px; height: 10px;
  border-radius: 999px;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
  // Approved → green; in-review/pending → orange; rejected/declined → red;
  // not-required / unknown → neutral gray.
  &--approved { background: #10b981; border-color: #047857; }
  &--pending  { background: #f59e0b; border-color: #b45309; }
  &--manual   { background: #f59e0b; border-color: #92400e; }
  &--removed  { background: #ef4444; border-color: #b91c1c; }
  &--unknown  { background: #d1d5db; border-color: rgba(0,0,0,0.12); }
  &--mute     { background: #d1d5db; border-color: rgba(0,0,0,0.12); }
}

// "Remove" button shown on Collection cards. Small red pill, top-right
// corner of the card. Fades in on hover for a less-crowded grid.
.fv-collection-remove {
  position: absolute;
  top: 8px; right: 8px;
  padding: 4px 9px;
  border-radius: 999px;
  border: 1px solid rgba(220, 38, 38, 0.4);
  background: #fff;
  color: #b91c1c;
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s, background 0.12s, color 0.12s, border-color 0.12s;
  &:hover {
    background: #b91c1c;
    color: #fff;
    border-color: #b91c1c;
  }
  .fv-prod:hover & { opacity: 1; }
}
// The collection card needs to anchor the remove button.
.fv-prod {
  position: relative;
}

// ===== Inline product detail (replaces grid inside Content Library) =====
.fv-back-inline {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 10px; border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle); color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  &:hover { border-color: rgba(91, 46, 207, 0.4); color: var(--accent-strong); }
}
.fv-detail {
  display: flex; flex-direction: column; gap: 16px;
}

// Large media banner — replaces the small thumbnail tile.
// Capped at 360px tall so very tall portraits don't take over the screen.
.fv-detail-media {
  width: 100%;
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  min-height: 220px;
  max-height: 360px;
}
.fv-detail-media-img {
  display: block;
  max-width: 100%;
  max-height: 360px;
  object-fit: contain;
}
.fv-detail-media-empty {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 60px 20px;
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
}

// Moderation status block (sits between media banner + headline)
.fv-mod {
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex; flex-direction: column; gap: 8px;
}
.fv-mod-head {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.fv-mod-eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--ink-muted);
}
.fv-mod-pill {
  display: inline-flex; align-items: center;
  padding: 3px 10px; border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  letter-spacing: 0.02em;
  &--approved { background: rgba(16, 185, 129, 0.16); color: #047857; }
  &--accepted { background: rgba(16, 185, 129, 0.16); color: #047857; }
  &--pending  { background: rgba(245, 158, 11, 0.16); color: #b45309; }
  &--manual   { background: rgba(245, 158, 11, 0.18); color: #92400e; }
  &--removed  { background: rgba(185, 28, 28, 0.14);  color: #b91c1c; }
  &--declined { background: rgba(185, 28, 28, 0.14);  color: #b91c1c; }
  &--empty    { background: var(--bg-panel);          color: var(--ink-muted); }
}
.fv-mod-count {
  margin-left: auto;
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: var(--ink-muted);
}
.fv-mod-list {
  display: flex; flex-direction: column; gap: 6px;
  padding-top: 6px;
  border-top: 1px dashed var(--border-soft);
}
.fv-mod-item {
  display: flex; align-items: center; gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: var(--ink-strong);
}
.fv-mod-item-thumb {
  width: 28px; height: 28px; flex-shrink: 0;
  border-radius: 6px; overflow: hidden;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fv-mod-item-type {
  font-size: 10.5px;
  color: var(--ink-muted);
  text-transform: uppercase; letter-spacing: 0.04em;
  font-weight: 700;
  min-width: 44px;
}
.fv-mod-item-status {
  font-weight: 700;
  color: var(--ink-strong);
}
.fv-mod-item-reason {
  flex: 1;
  text-align: right;
  font-size: 11px;
  font-style: italic;
  color: #b91c1c;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.fv-mod-reason {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: #b91c1c;
  strong { color: #7c2d12; }
}

.fv-detail-head {
  display: flex;
  // Allow the actions block to drop to a new row when the title (which now
  // demands its natural content width — see .fv-detail-headtitle below) gets
  // long enough that headmeta + actions can't share a single row.
  flex-wrap: wrap;
  column-gap: 14px;
  row-gap: 10px;
  align-items: flex-start;
  padding: 14px;
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
}
// Was `flex: 1; min-width: 0`. Auto basis with a reasonable floor lets the
// meta block grow with the title's content; when the row can't accommodate
// both blocks the actions wrap below.
.fv-detail-headmeta { flex: 1 1 auto; min-width: 280px; }
.fv-detail-eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--ink-muted);
  margin-bottom: 3px;
}
.fv-detail-headtitle {
  font-family: 'Inter', sans-serif;
  font-size: 18px; font-weight: 800;
  color: var(--ink-strong);
  margin-bottom: 4px;
  // The title now demands its natural content width on a single line. When
  // there isn't enough room beside the action buttons, the actions wrap to a
  // new row (see .fv-detail-head's flex-wrap). maxlength=80 on the input
  // bounds the worst case so this can't overflow the panel.
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
}
// Row that combines: "Title:" label + bold value + pencil-edit button.
.fv-detail-headtitle-row {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 4px;
  min-width: 0;
}
.fv-detail-headtitle-label {
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--ink-muted);
  flex-shrink: 0;
}
.fv-detail-headtitle-input {
  font-family: 'Inter', sans-serif;
  font-size: 18px; font-weight: 800;
  color: var(--ink-strong);
  background: var(--bg-panel);
  border: 1px solid var(--accent-ring);
  border-radius: 6px;
  padding: 4px 8px;
  flex: 1; min-width: 0;
  outline: none;
  &:focus { border-color: var(--accent-strong); box-shadow: 0 0 0 2px var(--accent-tint); }
}
.fv-detail-headtitle-edit {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 9px;
  border-radius: 6px;
  border: 1px solid var(--border-soft);
  background: rgba(16, 185, 129, 0.10);
  color: #047857;
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.1s, color 0.1s;
  &:hover { background: #047857; color: #fff; border-color: #047857; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid #047857; outline-offset: 1px; }
}

// Combined status card — Moderation + Consent side by side, always visible.
.fv-status {
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex; flex-direction: column; gap: 8px;
}
.fv-status-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.fv-status-cell {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  min-width: 0;
}
.fv-detail-headsub {
  font-family: 'Inter', sans-serif;
  font-size: 12px; color: var(--ink-muted);
  display: flex; gap: 6px; flex-wrap: wrap;
}
// Copy + Preview buttons in the header
.fv-detail-actions {
  display: flex; gap: 8px; flex-shrink: 0;
  // margin-left:auto keeps the buttons right-aligned both when they share a
  // row with the title meta and when they wrap to their own row below it.
  margin-left: auto;
}
.fv-detail-action {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px; border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel); color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
  white-space: nowrap;
  &:hover:not(:disabled) {
    border-color: var(--accent-ring);
    color: var(--accent-strong);
    background: var(--accent-tint);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &.is-copied {
    background: #e8fff4; border-color: #34d399; color: #059669;
  }
  &--primary {
    background: linear-gradient(135deg, #1a0a6b, #5b2ecf, #7040e0);
    color: #fff; border-color: transparent;
    &:hover:not(:disabled) {
      color: #fff; background: linear-gradient(135deg, #2a1a7b, #6b3ee0, #8050f0);
      box-shadow: 0 3px 12px rgba(91, 46, 207, 0.35);
    }
  }
}

// Variant picker (above the form, only on multi-clone clusters).
// Stands out with a subtle purple accent so the user sees they're scoping
// the editor to one specific Fangate product.
.fv-detail-variants {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 9px;
  background: linear-gradient(180deg, rgba(112, 64, 224, 0.07), rgba(112, 64, 224, 0.02));
  border: 1px solid rgba(91, 46, 207, 0.18);
  display: flex; flex-direction: column; gap: 5px;
  .fv-detail-label { margin-bottom: 0; color: #5b2ecf; }
}
.fv-detail-variants-select {
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, #5b2ecf 50%),
                    linear-gradient(135deg, #5b2ecf 50%, transparent 50%);
  background-position: calc(100% - 16px) center, calc(100% - 11px) center;
  background-size: 5px 5px;
  background-repeat: no-repeat;
  padding-right: 32px;
  cursor: pointer;
  font-weight: 600;
}
.fv-detail-form {
  display: flex; flex-direction: column;
  // Tightened from 14px — sections were floating too far apart and pushed
  // Save below the fold on common viewports.
  gap: 8px;
}

// Folder picker (dropdown + "new folder" inline editor)
.fv-folder-pick { position: relative; }
.fv-folder-btn {
  display: flex; align-items: center; gap: 8px;
  width: 100%; box-sizing: border-box;
  // Fixed (not min-) height so it stays exactly the same as
  // .fv-detail-price-wrap on the left column. Firefox's number input has
  // an intrinsic min-height (spinner controls) that would otherwise let
  // the price wrap grow past 42px when min-height was used.
  height: 42px;
  padding: 0 12px; border-radius: 9px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: var(--ink-strong);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.12s;
  &:hover, &.open { border-color: rgba(91, 46, 207, 0.5); }
  > span.is-empty { color: var(--ink-muted); font-style: italic; }
}
.fv-folder-menu {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0;
  z-index: 100;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 9px;
  box-shadow: 0 6px 22px rgba(11, 20, 20, 0.14);
  padding: 5px;
  max-height: 240px; overflow-y: auto;
}
.fv-folder-row {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 7px 9px;
  border-radius: 6px; border: none; background: transparent;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; color: var(--ink-strong);
  cursor: pointer; text-align: left;
  &:hover { background: var(--bg-subtle); }
  &--new {
    color: var(--accent-strong);
    font-weight: 700;
    &:hover { background: var(--accent-tint); }
  }
}
.fv-folder-row-name {
  flex: 1; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  &.is-muted { color: var(--ink-muted); font-style: italic; }
}
.fv-folder-sep {
  height: 1px; background: var(--border-soft); margin: 4px 6px;
}
.fv-folder-new {
  display: flex; gap: 6px; align-items: stretch;
  .fv-detail-input { flex: 1; min-width: 0; }
}
.fv-folder-cancel {
  flex-shrink: 0;
  width: 38px;
  border-radius: 9px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle);
  color: var(--ink-muted);
  font-size: 18px; line-height: 1;
  cursor: pointer;
  &:hover { color: #b91c1c; border-color: rgba(185, 28, 28, 0.4); }
}
.fv-detail-row { display: flex; flex-direction: column; gap: 4px; }
.fv-detail-row-2col {
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
  > div { display: flex; flex-direction: column; gap: 4px; }
}
.fv-detail-label {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--ink-muted);
  margin-bottom: 2px;
}
.fv-detail-input, .fv-detail-textarea {
  width: 100%; box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  font-size: 13px; color: var(--ink-strong);
  padding: 9px 12px;
  border-radius: 9px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle);
  outline: none;
  &:focus { border-color: rgba(91, 46, 207, 0.5); }
}
.fv-detail-textarea { resize: vertical; min-height: 60px; line-height: 1.45; }
.fv-detail-price-wrap {
  // Fixed 42px to match .fv-folder-btn on the right column.
  // Without an explicit height, Firefox's intrinsic <input type="number">
  // spinner-controls height pushes the wrap above 42px.
  height: 42px; box-sizing: border-box;
  display: flex; align-items: stretch;
  border: 1px solid var(--border-soft);
  border-radius: 9px; overflow: hidden;
  background: var(--bg-subtle);
}
.fv-detail-price-prefix {
  height: 100%;
  padding: 0 12px;
  display: flex; align-items: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 600;
  color: var(--ink-medium);
  border-right: 1px solid var(--border-soft);
  flex-shrink: 0;
}
.fv-detail-price-input {
  flex: 1; min-width: 0;
  height: 100%; box-sizing: border-box;
  border: none; background: transparent;
  font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 600;
  color: var(--ink-strong);
  padding: 0 12px; outline: none;
  // Kill the up/down spinner chevrons (same field also styled below).
  appearance: textfield;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }
}
.fv-detail-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: var(--ink-muted);
}
// Inline "Edit price" trigger that lives INSIDE the price field, flush
// against the right edge. Corporate green so it stands out as the brand's
// primary action color (same family as the "accepted consent" badge and
// the "Available for cashout" KPI).
.fv-detail-price-edit {
  display: inline-flex; align-items: center; gap: 4px;
  // Flush right — no margin between the button and the field edge. The
  // wrap's border-radius is preserved via overflow:hidden on the parent.
  padding: 5px 12px;
  height: 100%;
  border-radius: 0;
  border: none;
  border-left: 1px solid var(--border-soft);
  background: rgba(16, 185, 129, 0.10);
  color: #047857;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.1s, color 0.1s;
  flex-shrink: 0;
  &:hover { background: #047857; color: #fff; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid #047857; outline-offset: -2px; }
}
// (Spinner removal is consolidated into the main .fv-detail-price-input
// rule above so the cascade doesn't end up fighting with the modal/other
// number-input rules. Don't re-declare here.)
.fv-detail-flags {
  display: flex; flex-direction: column;
  gap: 2px;
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  background: var(--bg-subtle);
  padding: 4px 12px;
}
.fv-detail-flag {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; padding: 10px 0;
  border-bottom: 1px solid var(--border-soft);
  &:last-child { border-bottom: none; }
  &.is-disabled { opacity: 0.5; }
}
.fv-detail-flag-title {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 700;
  color: var(--ink-strong);
}
.fv-detail-flag-desc {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; color: var(--ink-muted);
  margin-top: 1px; line-height: 1.35;
}
.fv-switch {
  flex-shrink: 0;
  position: relative;
  width: 36px; height: 20px;
  border-radius: 999px;
  border: none; padding: 0;
  background: rgba(31, 42, 42, 0.22);
  cursor: pointer;
  transition: background 0.12s;
  &.on { background: linear-gradient(135deg, #5b2ecf, #7040e0); }
  &.disabled { cursor: not-allowed; }
}
.fv-switch-knob {
  position: absolute; top: 2px; left: 2px;
  width: 16px; height: 16px; border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(11, 20, 20, 0.2);
  transition: transform 0.15s;
  .fv-switch.on & { transform: translateX(16px); }
}
.fv-detail-err {
  padding: 8px 12px; border-radius: 8px;
  background: #fee2e2; color: #991b1b;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
}

// Co-creator consent section — chips list + inline adder
.fv-co {
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  background: var(--bg-subtle);
  padding: 12px 14px;
  display: flex; flex-direction: column; gap: 10px;
  // Session-only variant: amber dashed border, mounts the "manage at
  // fangate.info" notice in place of the broken picker.
  &--session-only {
    border-style: dashed;
    border-color: rgba(245, 158, 11, 0.4);
    background: linear-gradient(180deg, rgba(245, 158, 11, 0.06), transparent);
  }
}
.fv-co-title {
  display: flex; align-items: center; gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 700;
  color: #92400e;
}
// Corporate-green emphasis — same hex as the "Available for cashout"
// KPI card so the brand color stays consistent across the app.
.fv-co-emph {
  font-weight: 800;
  color: #047857;
}
.fv-co-desc {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; line-height: 1.45;
  color: var(--ink-muted);
}
.fv-co-loading, .fv-co-empty {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--ink-muted);
  font-style: italic;
}
.fv-co-error {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: #b91c1c;
  padding: 6px 10px; border-radius: 7px;
  background: #fee2e2;
}
.fv-co-chips {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.fv-co-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 4px 4px 10px;
  border-radius: 999px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  &--accepted { border-color: rgba(16, 185, 129, 0.45); background: rgba(16, 185, 129, 0.08); }
  &--declined { border-color: rgba(185, 28, 28, 0.4);  background: rgba(254, 226, 226, 0.6); }
  &--pending  { border-color: rgba(245, 158, 11, 0.35); background: rgba(245, 158, 11, 0.06); }
}
.fv-co-chip-name {
  font-weight: 700;
  color: var(--ink-strong);
  max-width: 160px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fv-co-chip-badge {
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.06em; text-transform: uppercase;
  padding: 1px 6px; border-radius: 999px;
  background: rgba(11, 20, 20, 0.06);
  color: var(--ink-medium);
  .fv-co-chip--accepted & { background: rgba(16, 185, 129, 0.16); color: #047857; }
  .fv-co-chip--declined & { background: rgba(185, 28, 28, 0.12);  color: #b91c1c; }
  .fv-co-chip--pending  & { background: rgba(245, 158, 11, 0.16); color: #b45309; }
}
.fv-co-chip-x {
  width: 18px; height: 18px;
  border-radius: 50%;
  border: none; padding: 0;
  background: rgba(11, 20, 20, 0.06);
  color: var(--ink-medium);
  font-size: 13px; line-height: 1;
  cursor: pointer;
  &:hover { background: #b91c1c; color: #fff; }
}
.fv-co-adder-trigger {
  display: flex;
}
.fv-co-add-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 10px; border-radius: 8px;
  border: 1px dashed rgba(91, 46, 207, 0.4);
  background: transparent;
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 700;
  cursor: pointer;
  &:hover { background: var(--accent-tint); border-style: solid; }
}
.fv-co-adder {
  // Vertical stack — input + search-status + results panel + action row,
  // each on its own row. Was originally flex-row, which compressed every
  // child into a single line and made the input un-clickable.
  display: flex; flex-direction: column; gap: 8px;
}
.fv-co-input {
  flex: 1; min-width: 0; box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; color: var(--ink-strong);
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  outline: none;
  &:focus { border-color: rgba(91, 46, 207, 0.5); }
}
.fv-co-confirm {
  padding: 8px 14px; border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #1a0a6b, #5b2ecf, #7040e0);
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}
.fv-co-cancel {
  padding: 8px 12px; border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}

// Search results panel (under the adder input)
.fv-co-adder-wrap {
  display: flex; flex-direction: column; gap: 4px;
  position: relative;
}
.fv-co-results {
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 9px;
  padding: 4px;
  box-shadow: 0 6px 18px rgba(11, 20, 20, 0.08);
  max-height: 240px; overflow-y: auto;
  display: flex; flex-direction: column;
  gap: 2px;
}
.fv-co-results-loading, .fv-co-results-empty {
  padding: 8px 10px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
  font-style: italic;
}
.fv-co-result {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 6px 8px;
  border-radius: 6px; border: none;
  background: transparent;
  text-align: left;
  // The row itself isn't clickable anymore — only the per-row "Tag creator"
  // button fires the action — so no cursor:pointer on the row.
  &:hover { background: var(--bg-subtle); }
}
.fv-co-result-avatar {
  width: 28px; height: 28px;
  border-radius: 50%; overflow: hidden;
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 700;
  color: var(--ink-medium);
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fv-co-result-meta {
  display: flex; flex-direction: column; gap: 1px;
  min-width: 0; flex: 1;
}
.fv-co-result-name {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 700;
  color: var(--ink-strong);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fv-co-result-sub {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; color: var(--ink-muted);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

// ===== New tagging-UI extensions (used by the API-backed flow) ============
// Status-coded chip background for existing consent tags.
.fv-co-chip {
  &--pending {
    background: rgba(245, 158, 11, 0.10);
    border-color: rgba(245, 158, 11, 0.32);
    .fv-co-chip-status { color: #b45309; }
  }
  &--accepted {
    background: rgba(16, 185, 129, 0.10);
    border-color: rgba(16, 185, 129, 0.32);
    .fv-co-chip-status { color: #047857; }
  }
  &--declined {
    background: rgba(220, 38, 38, 0.10);
    border-color: rgba(220, 38, 38, 0.32);
    .fv-co-chip-status { color: #b91c1c; }
  }
}
.fv-co-chip-avatar {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px;
  border-radius: 50%;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  font-family: 'Libre Baskerville', serif;
  font-size: 9px; font-weight: 700;
  color: var(--accent-strong);
  flex-shrink: 0;
}
.fv-co-chip-status {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.04em; text-transform: uppercase;
  margin-left: 4px;
}

// Add-co-creator trigger + adder action row.
.fv-co-actions {
  margin-top: 6px;
}
.fv-co-addbtn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 11px;
  border-radius: 7px;
  border: 1px dashed rgba(91, 46, 207, 0.4);
  background: transparent;
  color: #5b2ecf;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s;
  &:hover {
    background: rgba(91, 46, 207, 0.06);
    border-color: #5b2ecf;
  }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid #7040e0; outline-offset: 2px; }
}
.fv-co-adderactions {
  display: flex; justify-content: flex-end; gap: 6px;
  margin-top: 8px;
}
.fv-co-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: var(--ink-muted);
  margin-top: 4px;
  font-style: italic;
}
.fv-co-err {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: #b91c1c;
  margin-top: 4px;
  padding: 5px 8px;
  border-radius: 6px;
  background: rgba(220, 38, 38, 0.08);
}
.fv-co-result-email {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; color: var(--ink-muted);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

// Always-visible search input in the new tag-from-result UX.
.fv-co-searchinput {
  margin-top: 4px;
}

// Per-row "Tag creator" button — small green pill, corporate accent.
// Click fires POST /consents/store-or-send for that specific creator.
.fv-co-tagbtn {
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 7px;
  border: none;
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.1s, color 0.1s;
  &:hover:not(:disabled) { background: #047857; color: #fff; }
  &:disabled { opacity: 0.6; cursor: wait; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid #047857; outline-offset: 2px; }
}

// Email-invite fallback when the search input is email-shaped and
// Fangate returned no Fangate-user matches. Same green pill as
// .fv-co-tagbtn but wider (full row) and with an envelope icon.
.fv-co-noresults {
  display: flex; flex-direction: column; gap: 8px;
  margin-top: 2px;
}
.fv-co-invitebtn {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 7px;
  padding: 9px 14px;
  border-radius: 8px;
  border: 1.5px solid rgba(16, 185, 129, 0.35);
  background: rgba(16, 185, 129, 0.06);
  color: #047857;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 700;
  cursor: pointer;
  align-self: flex-start;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
  &:hover:not(:disabled) {
    background: #047857; color: #fff; border-color: #047857;
  }
  &:disabled { opacity: 0.6; cursor: wait; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid #047857; outline-offset: 2px; }
}
.fv-detail-foot {
  display: flex; justify-content: flex-end; gap: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-soft);
  margin-top: 4px;
}
.fv-detail-cancel {
  padding: 8px 14px; border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  &:hover { color: var(--ink-strong); }
}
.fv-detail-save {
  padding: 8px 16px; border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #1a0a6b, #5b2ecf, #7040e0);
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.65; cursor: not-allowed; }
  &:hover:not(:disabled) {
    box-shadow: 0 3px 12px rgba(91, 46, 207, 0.32);
  }
}

// ===== Placeholder cards =====
.fv-placeholder {
  background: linear-gradient(180deg, rgba(112, 64, 224, 0.05), transparent);
  border: 1.5px dashed rgba(112, 64, 224, 0.3);
  border-radius: 14px;
  padding: 36px 24px;
  text-align: center;
}
.fv-placeholder-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 52px; height: 52px;
  border-radius: 50%;
  background: rgba(112, 64, 224, 0.12);
  margin-bottom: 10px;
}
// Larger / more inviting variant used as the Vault landing screen (no
// module picked yet). Re-skinned in Creator-Chat corporate teal (not the
// Fangate purple the other placeholders below use) so the empty state
// reads as OUR product surface, not a Fangate panel.
.fv-placeholder--lead {
  padding: 64px 32px;
  margin-top: 24px;
  background: linear-gradient(180deg, rgba(13, 148, 136, 0.07), transparent);
  border-style: solid;
  border-color: rgba(13, 148, 136, 0.22);
  // Override base purple typography for the lead variant.
  .fv-placeholder-title {
    color: var(--accent-strong);
  }
  .fv-placeholder-body {
    color: var(--ink-medium);
    code, strong {
      color: var(--accent-strong);
    }
  }
}
.fv-placeholder-icon--lead {
  width: 68px; height: 68px;
  background: rgba(13, 148, 136, 0.12);
  // currentColor on the SVG inherits from this rule, so a single colour
  // change here flips the icon stroke too.
  color: var(--accent);
  box-shadow: 0 4px 18px rgba(15, 118, 110, 0.18);
}
.fv-placeholder-title {
  font-family: 'Inter', sans-serif;
  font-size: 15px; font-weight: 800;
  color: #1a0a6b;
  margin-bottom: 6px;
}
.fv-placeholder-body {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; line-height: 1.5;
  color: #5b2ecf;
  max-width: 480px; margin: 0 auto;
  code {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11.5px;
    background: rgba(255, 255, 255, 0.6);
    padding: 1px 5px;
    border-radius: 4px;
  }
}
</style>
